'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import GanttChart from '@/components/GanttChart';
import ProjectHistoryModal from '@/components/ProjectHistoryModal';
import dynamic from 'next/dynamic';

const BriefingHistoryModal = dynamic(() => import('@/components/BriefingHistoryModal'), { ssr: false });
const CodingBoard = dynamic(() => import('@/components/CodingBoard'), { ssr: false });
const AgentTrainingBoard = dynamic(() => import('@/components/AgentTrainingBoard'), { ssr: false });
const AgentChatPanel = dynamic(() => import('@/components/AgentChatPanel'), { ssr: false });
const RAGAnalyticsPanel = dynamic(() => import('@/components/RAGAnalyticsPanel'), { ssr: false });
import { DndSchedulerProvider, DroppableDayColumn } from '@/components/DndScheduler';
import JumpToToday from "@/components/JumpToToday";
import {
    Calendar, Share2, Plus, CheckCircle2, Clock, PlayCircle,
    Edit2, BrainCircuit, Activity, Plane, HardDrive, MessageSquare,
    History, Bot, Send, AlertTriangle, MessageCircle, Network,
    Pencil, X, GripVertical, Save, Trash2, Bell, Zap, RefreshCw,
    ChevronLeft, ChevronRight, ChevronDown, FolderOpen, MoreVertical, Maximize2, Minimize2
} from 'lucide-react';
import NodeHealthWidget from "@/components/NodeHealthWidget";
import EmojiIcon from '@/components/EmojiIcon';


type TaskStatus = 'todo' | 'in-progress' | 'done' | 'issue';

interface Task {
    id: string;
    projectId: string;
    title: string;
    assignee?: string;
    assignees?: string;
    memo?: string;
    status: TaskStatus;
    date: string; // YYYY-MM-DD
    aiTags?: string[];
    sortOrder?: number;
}

interface Project {
    id: string;
    name: string;
    color: string;
}

const PROJECTS: Project[] = []; // fallback
const INITIAL_TASKS: Task[] = []; // fallback

// ── 프로젝트 별칭 → NAS ID 매핑 (동적 + 정적 폴백) ──
// fuse.js 퍼지 매칭으로 동적 매핑, 하드코딩은 폴백용으로 유지
import Fuse from 'fuse.js';
import {
  mergeNasEntries,
  resolveNasProjectFromText,
  NAS_CONTEXT_OVERRIDES,
  type NasMatchEntry,
} from '@/lib/nas-project-context';

interface NasAliasEntry extends NasMatchEntry {}

// ── Layer 0: NAS 매칭 불필요 프로젝트 (경영/공통 등) ──
// 이 프로젝트들은 NAS 폴더가 없는 것이 정상 → "매칭 불필요" 표시
const NON_NAS_PROJECT_KEYWORDS = [
    '경영', '공통', '일반', '인사', '총무', '회계', '급여', '세금',
    '매출', '복리후생', '사무실', '비품', '교육훈련', '워크숍',
    '연말정산', '4대보험', '명절', '연차', '휴가', '예비군',
];

function isNonNasProject(projectName: string): boolean {
    const n = projectName.replace(/[()（）\s]/g, '').toLowerCase();
    return NON_NAS_PROJECT_KEYWORDS.some(kw => n.includes(kw));
}

// ⚠️ 정적 폴백 (NAS 스캔 실패 시 사용)
// 🔴 실제 NAS 폴더명 기준 (Finder 확인 2026-03-27) — 할루시네이션 ID 금지!
const STATIC_NAS_FALLBACK: NasAliasEntry[] = [
    // ── 2026 프로젝트 ──
    { aliases: ['장성', '장성btl', '장성BTL', '육군장성', '육군 장성', '장성교육', '장성 교육', '장성모듈', '장성컬러'], nasId: 'ID_2603_육군 장성 교육시설_BTL', nasPrefix: 'ID_2603', fullName: '육군 장성 교육시설 BTL' },
    { aliases: ['광교', '광교중흥', '중흥', '광교 중흥'], nasId: 'ID_2604_광교 중흥', nasPrefix: 'ID_2604', fullName: '광교 중흥' },
    { aliases: ['감매', '감매역', '감매역세권', '감매유치원', '감매역세권유치원'], nasId: 'ID_2605_감매역세권유치원', nasPrefix: 'ID_2605', fullName: '감매역세권유치원' },
    { aliases: ['00부대', '부대'], nasId: 'ID_2601_00부대', nasPrefix: 'ID_2601', fullName: '00부대' },
    { aliases: ['장지', '장지차고지', '장지차고지기술제안'], nasId: 'ID_2602_장지차고지_기술제안', nasPrefix: 'ID_2602', fullName: '장지차고지 기술제안' },

    // ── 2025 프로젝트 ──
    { aliases: ['평택', '평택교육청', '평택교육지원청', '평택교육', '컨퍼런스홀', '컨퍼런스', '평택인테리어'], nasId: 'ID_2506_평택교육지원청', nasPrefix: 'ID_2506', fullName: '평택교육지원청' },
    { aliases: ['웅촌', '웅촌초', '울산웅촌', '울산 웅촌', '웅촌초등'], nasId: 'ID_2543_웅촌초', nasPrefix: 'ID_2543', fullName: '웅촌초등학교' },
    { aliases: ['교통대', '교원대', '한국교통대', '교통대btl', '충북교원대'], nasId: 'ID_2535_한국교통대_BTL', nasPrefix: 'ID_2535', fullName: '한국교통대 BTL' },
    { aliases: ['함양', '함양도서관', '함양군', '함양군립'], nasId: 'ID_2514_함양도서관', nasPrefix: 'ID_2514', fullName: '함양도서관' },
    { aliases: ['분당', '분당보건소', '분당구보건소', '성남분당', '분당보건'], nasId: 'ID_2552_분당구보건소', nasPrefix: 'ID_2552', fullName: '분당구보건소' },
    { aliases: ['중봉', '중봉도서관', '중봉s클래스', '중봉리모델링', '안성중봉'], nasId: 'ID_2551_중봉도서관 리모델링', nasPrefix: 'ID_2551', fullName: '중봉도서관 리모델링' },
    { aliases: ['옥포', '옥포초', '옥포초등'], nasId: 'ID_2510_옥포초', nasPrefix: 'ID_2510', fullName: '옥포초등학교' },
    { aliases: ['대곡', '대곡고', '대곡고등'], nasId: 'ID_2531_대곡고', nasPrefix: 'ID_2531', fullName: '대곡고등학교' },
    { aliases: ['장안', '장안1초', '장안1초등학교', '장안1'], nasId: 'ID_2539_장안1초', nasPrefix: 'ID_2539', fullName: '장안1초등학교' },
    { aliases: ['에코1초', '에코1', '에코초등'], nasId: 'ID_2540_에코1초', nasPrefix: 'ID_2540', fullName: '에코1초등학교' },
    { aliases: ['에코3초', '에코3'], nasId: 'ID_2610_에코3초', nasPrefix: 'ID_2610', fullName: '에코3초등학교' },
    { aliases: ['벡스코', '벡스코기술제안', 'bexco'], nasId: 'ID_2554_벡스코 기술제안', nasPrefix: 'ID_2554', fullName: '벡스코 기술제안' },
    { aliases: ['밀양', '밀양도서관', '밀양영어도서관', '밀양시립', '밀양시립도서관'], nasId: 'ID_2532_밀양도서관', nasPrefix: 'ID_2532', fullName: '밀양도서관' },

    // ── 2024 프로젝트 ──
    { aliases: ['태릉', '태능', '태능여올림', '태능여올림도서관', '태릉도서관'], nasId: 'ID_2430_태능여올림도서관', nasPrefix: 'ID_2430', fullName: '태능여올림도서관' },

    // ── 특허/기타 ──
    { aliases: ['건축공정계획', '502', '스냅코덱스', 'snapcodex'], nasId: 'S_스냅코덱스_특허', nasPrefix: 'S_특허', fullName: '건축공정계획 502 (스냅코덱스 특허)' },
];

// 동적 NAS 매핑 캐시 (1시간 TTL)
let dynamicNasProjects: NasAliasEntry[] = [];
let nasProjectsLoadedAt = 0;
const NAS_PROJECTS_TTL_MS = 60 * 60 * 1000;
let fuseIndex: Fuse<{ alias: string; entry: NasAliasEntry }> | null = null;

function buildFuseIndex(entries: NasAliasEntry[]) {
    const items = entries.flatMap(entry =>
        entry.aliases.map(alias => ({ alias, entry }))
    );
    fuseIndex = new Fuse(items, {
        keys: ['alias'],
        threshold: 0.45,  // 기존 0.4 → 0.45로 완화
        includeScore: true,
        minMatchCharLength: 2,
        ignoreLocation: true,
    });
}

function resolveNasProject(title: string, projectName: string): { nasId: string; nasPrefix: string; fullName: string } | null {
    const allEntries = dynamicNasProjects.length > 0
        ? dynamicNasProjects
        : mergeNasEntries([], [...STATIC_NAS_FALLBACK, ...NAS_CONTEXT_OVERRIDES]);
    const result = resolveNasProjectFromText(title, projectName, allEntries);
    if (!result) {
        if (isNonNasProject(projectName)) return null;
        return null;
    }
    return {
        nasId: result.entry.nasId,
        nasPrefix: result.entry.nasPrefix,
        fullName: result.entry.fullName,
    };
}

interface NotifyLog {
    time: string;
    agent: string;
    msg: string;
    channels: string[];
    type: 'add' | 'delete' | 'modify' | 'move' | 'system';
}

type LiveLogKind = 'schedule' | 'nas' | 'agent' | 'pipeline' | 'system';
type LiveLogFilter = LiveLogKind | 'all';

interface LiveLogItem {
    id: string;
    timestamp: string;
    kind: LiveLogKind;
    action: string;
    title: string;
    summary?: string;
    agent: string;
    channel: string;
    taskId?: string | null;
    projectId?: string | null;
    filePath?: string | null;
    owner?: string | null;
    workType?: string | null;
    watchRoot?: {
        id: string;
        label: string;
        kind: string;
        nasPath: string;
    } | null;
    meta?: Record<string, any>;
}

interface LiveLogStats {
    total: number;
    schedule: number;
    nas: number;
    agent: number;
    pipeline: number;
    system: number;
    latestAt?: string | null;
}

interface LiveLogWatchRoot {
    id: string;
    label: string;
    owner: string;
    kind: string;
    nasPath: string;
}

const LIVE_LOG_FILTERS: Array<{ id: LiveLogFilter; label: string }> = [
    { id: 'all', label: '전체' },
    { id: 'schedule', label: '일정' },
    { id: 'nas', label: 'NAS' },
    { id: 'agent', label: '에이전트' },
    { id: 'pipeline', label: '파이프라인' },
    { id: 'system', label: '시스템' },
];

const LIVE_LOG_KIND_LABELS: Record<LiveLogKind, string> = {
    schedule: '일정',
    nas: 'NAS',
    agent: '에이전트',
    pipeline: '파이프라인',
    system: '시스템',
};

const LIVE_LOG_KIND_STYLES: Record<LiveLogKind, string> = {
    schedule: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
    nas: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    agent: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
    pipeline: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    system: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
};

const LIVE_LOG_WORK_TYPE_LABELS: Record<string, string> = {
    'cad-production': 'CAD/도면',
    'estimate-or-boq': '견적/내역',
    presentation: '발표/보고',
    document: '문서',
    'visual-asset': '시각자료',
    modeling: '3D/모델링',
    'general-work': '일반업무',
};

function formatLiveLogTime(value?: string | null) {
    if (!value) return '-';
    return new Date(value).toLocaleString('ko-KR', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
}

const INITIAL_HISTORY: NotifyLog[] = [
    { time: '10분 전', agent: 'Tony', msg: 'NAS(124.55.60.40) 신규 도면 파일 감지 및 벡터스토어 인덱싱 완료', channels: ['NAS'], type: 'system' },
    { time: '1시간 전', agent: 'Meety', msg: '카카오톡 비즈니스 채널: "신갈초 디자인 보고서 진행상황 공유" 클라이언트 발송 완료', channels: ['카카오톡'], type: 'system' },
    { time: '2시간 전', agent: 'Rudy', msg: '과거 3년치 교육시설 RAG 데이터 기반 사송2초 디자인 컨셉 1차 스케치 생성', channels: ['RAG'], type: 'system' },
];

function formatDateKR(dateStr: string): string {
    // noon UTC 사용으로 어떤 환경에서도 날짜 밀림 방지
    const d = new Date(dateStr + 'T12:00:00Z');
    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
    return `${d.getUTCMonth() + 1}/${d.getUTCDate()}(${dayNames[d.getUTCDay()]})`;
}

const PROJECT_LEGENDS = [
    { label: '제안서', color: 'bg-[#e74c3c]' },
    { label: '설계도서', color: 'bg-gray-400' },
    { label: '내역', color: 'bg-gray-800' },
    { label: '경영지원', color: 'bg-[#a3e4d7]' },
    { label: '회의', color: 'bg-[#af7ac5]' },
    { label: '계약정산', color: 'bg-[#f5b041]' },
    { label: '견적제출', color: 'bg-[#a9dfbf]' },
    { label: '업무연락', color: 'bg-[#5dade2]' },
    { label: '동영상', color: 'bg-[#58d68d]' },
    { label: '도면제출', color: 'bg-[#ec7063]' },
    { label: '납품제출', color: 'bg-[#1abc9c]' }
];

const getLegendColorClass = (title: string, projectName: string) => {
    if (title.includes('제안') || title.includes('PT') || title.includes('프레젠')) return 'bg-[#e74c3c]';
    if (title.includes('보고') || title.includes('연락')) return 'bg-[#5dade2]';
    if (title.includes('회의') || title.includes('취합') || title.includes('협의') || title.includes('자문')) return 'bg-[#af7ac5]';
    if (title.includes('도면') || title.includes('디자인')) return 'bg-[#ec7063]';
    if (title.includes('내역') || title.includes('원가')) return 'bg-gray-800';
    if (title.includes('견적')) return 'bg-[#a9dfbf]';
    if (title.includes('제출') || title.includes('납품') || title.includes('업로드')) return 'bg-[#1abc9c]';
    if (title.includes('CG') || title.includes('동영상') || title.includes('렌더')) return 'bg-[#58d68d]';
    if (title.includes('정산') || title.includes('계약') || title.includes('청구')) return 'bg-[#f5b041]';
    if (title.includes('휴일') || title.includes('연차') || title.includes('반차') || title.includes('예비군')) return 'bg-[#ec7063]';
    if (title.includes('도서') || title.includes('설계')) return 'bg-gray-400';
    if (projectName && projectName.includes('경영')) return 'bg-[#a3e4d7]';
    return 'bg-[#5dade2]';
};

const generateWeeks = (offset: number = 0) => {
    const weeks = [];
    const now = new Date();
    // Start from the beginning of the current week (Sunday), then apply offset
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay() + (offset * 7));
    startOfWeek.setHours(0, 0, 0, 0);
    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];

    for (let w = 0; w < 6; w++) {
        const weekDays = [];
        for (let d = 0; d < 7; d++) {
            const current = new Date(startOfWeek.getTime() + (w * 7 + d) * 86400000);
            const y = current.getFullYear();
            const m = String(current.getMonth() + 1).padStart(2, '0');
            const day = String(current.getDate()).padStart(2, '0');
            weekDays.push({
                date: `${y}-${m}-${day}`,
                display: `${Number(m)}/${Number(day)}`,
                dayName: dayNames[current.getDay()],
                isSun: current.getDay() === 0,
                isSat: current.getDay() === 6
            });
        }
        weeks.push(weekDays);
    }
    return weeks;
}

const todayStr = (() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
})();


const ASSIGNEE_OPTIONS = [
    'Poby', '경철', '호율', '지혜', '태문', '현우', '다윤', '현일', '윤지', 'Jessi', 'Shon', 'Benny', 'Tony', 'Roony', 'Meety', 'Dony'
];

export default function SchedulerTab({ isDark = true, isAuthenticated = false, defaultProjectId }: { isDark?: boolean; isAuthenticated?: boolean; defaultProjectId?: string }) {
    const [projects, setProjects] = useState<Project[]>(PROJECTS);
    const [tasks, setTasks] = useState<Task[]>(INITIAL_TASKS);
    const [mounted, setMounted] = useState(false);
    const [isEditing, setIsEditing] = useState<string | null>(null);
    const [editValue, setEditValue] = useState('');
    const [activeTab, setActiveTab] = useState<'scheduler' | 'analytics' | 'history' | 'logs' | 'gantt' | 'briefing' | 'backup' | 'coding' | 'training'>('scheduler');
    const [briefingData, setBriefingData] = useState<{ markdown: string; data: any; insights?: any } | null>(null);
    const [isBriefingHistoryOpen, setIsBriefingHistoryOpen] = useState(false);
    const [briefingLoading, setBriefingLoading] = useState(false);
    const [backupStatus, setBackupStatus] = useState<any>(null);
    const [backupLoading, setBackupLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    // 전체화면: 브라우저 Fullscreen API 우선, 미지원(iOS Safari 등)·거부 시 화면 고정 레이어로 대체
    const rootRef = useRef<HTMLDivElement>(null);
    const [nativeFullscreen, setNativeFullscreen] = useState(false);
    const [cssFullscreen, setCssFullscreen] = useState(false);
    const isFullscreen = nativeFullscreen || cssFullscreen;
    useEffect(() => {
        const onChange = () => setNativeFullscreen(document.fullscreenElement === rootRef.current);
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);
    useEffect(() => {
        if (!cssFullscreen) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setCssFullscreen(false); };
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prevOverflow; };
    }, [cssFullscreen]);
    // 진입/종료 요청은 비동기라 처리 중 연타는 무시한다(native·CSS 상태 동시 true 방지).
    const fullscreenPendingRef = useRef(false);
    const toggleFullscreen = useCallback(() => {
        if (fullscreenPendingRef.current) return;
        if (cssFullscreen) { setCssFullscreen(false); return; }
        if (document.fullscreenElement) {
            fullscreenPendingRef.current = true;
            document.exitFullscreen().catch(() => {}).finally(() => { fullscreenPendingRef.current = false; });
            return;
        }
        const el = rootRef.current;
        if (!el?.requestFullscreen) { setCssFullscreen(true); return; }
        fullscreenPendingRef.current = true;
        el.requestFullscreen()
            .catch(() => { if (!document.fullscreenElement) setCssFullscreen(true); })
            .finally(() => { fullscreenPendingRef.current = false; });
    }, [cssFullscreen]);
    const [draggedTask, setDraggedTask] = useState<string | null>(null);
    const [showAddModal, setShowAddModal] = useState<string | null>(null); // date string
    const [newTaskTitle, setNewTaskTitle] = useState('');
    const addInputRef = useRef<HTMLInputElement>(null);
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
    const [notifyHistory, setNotifyHistory] = useState<NotifyLog[]>(INITIAL_HISTORY);
    const [notifyToast, setNotifyToast] = useState<{ show: boolean; msg: string; type: 'success' | 'error' }>({ show: false, msg: '', type: 'success' });
    const [weekOffset, setWeekOffset] = useState(0);
    const [reportLoading, setReportLoading] = useState(false);
    const [reportData, setReportData] = useState<{ markdown: string; report: any } | null>(null);
    const [showReport, setShowReport] = useState(false);
    const [flowEvents, setFlowEvents] = useState<Record<string, Array<{ id: string; title: string; type: string; source: string; color: string; status?: string; projectName?: string; projectId?: string }>>>({});
    const [showFlowEvents, setShowFlowEvents] = useState(true);
    const [showAnnouncement, setShowAnnouncement] = useState(false);
    const [showDashboard, setShowDashboard] = useState(false);
    const [historyModalTask, setHistoryModalTask] = useState<{ id: string, title: string, projectId: string, date: string, projectName: string } | null>(null);
    const [taskContextMenu, setTaskContextMenu] = useState<{ x: number, y: number, task: Task } | null>(null);
    const [subtaskAddTask, setSubtaskAddTask] = useState<{ x: number, y: number, task: Task } | null>(null);
    const [subtaskTitle, setSubtaskTitle] = useState('');
    const [subtaskAssignee, setSubtaskAssignee] = useState('');
    // Premium Agent Chat state (AgentChatPanel internally manages its own input/messages)
    const [showAgentChat, setShowAgentChat] = useState(false);
    const [showMonthPicker, setShowMonthPicker] = useState(false);
    const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
    const [showToolbarMore, setShowToolbarMore] = useState(false);
    const [legendFilter, setLegendFilter] = useState<string | null>(null);
    // Flow 프로젝트 캐시 — 스케줄러 프로젝트명 → Flow 프로젝트 ID 매핑
    const [flowProjects, setFlowProjects] = useState<Array<{ id: string; name: string; color: string }>>([]);
    const [showAudit, setShowAudit] = useState(false);
    const [auditLogs, setAuditLogs] = useState<any[]>([]);
    const [auditLoading, setAuditLoading] = useState(false);
    const [liveLogs, setLiveLogs] = useState<LiveLogItem[]>([]);
    const [liveLogStats, setLiveLogStats] = useState<LiveLogStats | null>(null);
    const [liveLogLoading, setLiveLogLoading] = useState(false);
    const [liveLogFilter, setLiveLogFilter] = useState<LiveLogFilter>('all');
    const [liveLogUpdatedAt, setLiveLogUpdatedAt] = useState<string | null>(null);
    const [liveLogWatchRoots, setLiveLogWatchRoots] = useState<LiveLogWatchRoot[]>([]);

    const refreshAudit = useCallback(() => {
        setAuditLoading(true);
        fetch('/api/schedule-audit', { signal: AbortSignal.timeout(5000) })
            .then(res => res.json())
            .then(data => setAuditLogs(data.entries || []))
            .catch(e => console.error('[Audit] fetch error:', e))
            .finally(() => setAuditLoading(false));
    }, []);

    useEffect(() => {
        if (showAudit) refreshAudit();
    }, [showAudit, refreshAudit]);

    const refreshLiveLogs = useCallback((silent = false) => {
        if (!silent) setLiveLogLoading(true);
        fetch(`/api/schedule/live-logs?limit=100&type=${liveLogFilter}`, { signal: AbortSignal.timeout(5000) })
            .then(res => res.json())
            .then(data => {
                setLiveLogs(data.logs || []);
                setLiveLogStats(data.stats || null);
                setLiveLogWatchRoots(data.watchRoots || []);
                setLiveLogUpdatedAt(data.generatedAt || new Date().toISOString());
            })
            .catch(e => console.error('[LiveLogs] fetch error:', e))
            .finally(() => {
                if (!silent) setLiveLogLoading(false);
            });
    }, [liveLogFilter]);

    useEffect(() => {
        if (!mounted || activeTab !== 'logs') return;
        refreshLiveLogs();
    }, [mounted, activeTab, liveLogFilter, refreshLiveLogs]);

    useEffect(() => {
        if (!mounted || activeTab !== 'logs') return;
        const timer = window.setInterval(() => refreshLiveLogs(true), 5000);
        return () => window.clearInterval(timer);
    }, [mounted, activeTab, refreshLiveLogs]);
    const router = useRouter();

    const wheelCooldownRef = useRef(false);
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
    const handleCalendarWheel = useCallback((e: React.WheelEvent) => {
        if (wheelCooldownRef.current) return;
        if (Math.abs(e.deltaX) > 20) {
            setWeekOffset(prev => e.deltaX > 0 ? prev + 1 : prev - 1);
            wheelCooldownRef.current = true;
            setTimeout(() => { wheelCooldownRef.current = false; }, 300);
        } else if (Math.abs(e.deltaY) > 20) {
            setWeekOffset(prev => e.deltaY > 0 ? prev + 1 : prev - 1);
            wheelCooldownRef.current = true;
            setTimeout(() => { wheelCooldownRef.current = false; }, 300);
        }
    }, []);

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }, []);

    const handleTouchEnd = useCallback((e: React.TouchEvent) => {
        if (!touchStartRef.current || wheelCooldownRef.current) return;
        const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
        const dy = e.changedTouches[0].clientY - touchStartRef.current.y;
        touchStartRef.current = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
            setWeekOffset(prev => dx < 0 ? prev + 1 : prev - 1);
            wheelCooldownRef.current = true;
            setTimeout(() => { wheelCooldownRef.current = false; }, 400);
        }
    }, []);

    const WEEKS = generateWeeks(weekOffset);
    const startYMD = WEEKS.length > 0 ? WEEKS[0][0].date.split('-') : [];
    const endYMD = WEEKS.length > 0 ? WEEKS[WEEKS.length - 1][6].date.split('-') : [];
    const weekRangeLabel = WEEKS.length > 0 ? `${startYMD[0]}.${WEEKS[0][0].display} ~ ${endYMD[0] !== startYMD[0] ? endYMD[0] + '.' : ''}${WEEKS[WEEKS.length - 1][6].display} (${WEEKS.length}주)` : '';

    // 브리핑 탭 자동 로드 — 탭 진입 시 insights + briefing 동시 fetch
    useEffect(() => {
        if (activeTab !== 'briefing' || briefingData || briefingLoading) return;
        setBriefingLoading(true);
        Promise.all([
            fetch('/api/schedule/daily-briefing?format=markdown').then(r => r.json()),
            fetch('/api/schedule/project-insights?range=week').then(r => r.json()),
        ]).then(([briefing, insights]) => {
            setBriefingData({ ...briefing, insights: insights.success ? insights : null });
            setBriefingLoading(false);
        }).catch(() => setBriefingLoading(false));
    }, [activeTab, briefingData, briefingLoading]);

    // Flow 이벤트 로드 — 현재 보이는 날짜 범위에 맞춰 가져오기
    useEffect(() => {
        if (!mounted) return;
        const dates = WEEKS.flat().map(d => d.date);
        if (dates.length === 0) return;
        const firstDate = dates[0];
        const lastDate = dates[dates.length - 1];
        const startYear = parseInt(firstDate.split('-')[0]);
        const startMonth = parseInt(firstDate.split('-')[1]);
        const endYear = parseInt(lastDate.split('-')[0]);
        const endMonth = parseInt(lastDate.split('-')[1]);

        // Fetch flow events for the months covered
        const months = new Set<string>();
        for (let y = startYear; y <= endYear; y++) {
            const mStart = y === startYear ? startMonth : 1;
            const mEnd = y === endYear ? endMonth : 12;
            for (let m = mStart; m <= mEnd; m++) {
                months.add(`${y}-${m}`);
            }
        }

        Promise.all(
            Array.from(months).map(ym => {
                const [y, m] = ym.split('-');
                return fetch(`/api/calendar/unified?year=${y}&month=${m}&source=flow`)
                    .then(r => r.json())
                    .catch(() => null);
            })
        ).then(results => {
            const merged: typeof flowEvents = {};
            for (const res of results) {
                if (!res?.events) continue;
                for (const [date, evts] of Object.entries(res.events)) {
                    if (!merged[date]) merged[date] = [];
                    merged[date].push(...(evts as any[]));
                }
            }
            setFlowEvents(merged);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mounted, weekOffset]);

    // ── Flow 프로젝트 이름 → 스케줄러 프로젝트 매핑 (퍼지 매칭) ──
    const navigateToFlowProject = useCallback((schedulerProjectName: string) => {
        if (!schedulerProjectName || flowProjects.length === 0) {
            // Flow 프로젝트 미로드 시 /flow로 이동
            router.push('/flow');
            return;
        }
        const normalized = schedulerProjectName.replace(/[()（）\s]/g, '').toLowerCase();
        // 1차: 정확 포함 매칭
        let match = flowProjects.find(fp => {
            const fpN = fp.name.replace(/[()（）\s]/g, '').toLowerCase();
            return fpN.includes(normalized) || normalized.includes(fpN);
        });
        // 2차: 한글 토큰 교차 매칭 (2자 이상)
        if (!match) {
            const tokens = schedulerProjectName.match(/[가-힣]{2,}/g) || [];
            for (const token of tokens) {
                match = flowProjects.find(fp => fp.name.includes(token));
                if (match) break;
            }
        }
        if (match) {
            router.push(`/flow/project/${match.id}`);
        } else {
            router.push('/flow');
        }
    }, [flowProjects, router]);

    useEffect(() => {
        setMounted(true);
        fetch('/api/schedule')
            .then(res => res.json())
            .then(data => {
                if (data.projects) setProjects(data.projects);
                if (data.tasks) setTasks(data.tasks);
            })
            .catch(console.error);

        // ── Flow 프로젝트 목록 캐싱 (ID 매핑용) ──
        fetch('/api/flow/projects')
            .then(res => res.json())
            .then(data => {
                if (data.projects) {
                    setFlowProjects(data.projects.map((p: any) => ({ id: p.id, name: p.name, color: p.color })));
                    console.log(`[Scheduler] Flow 프로젝트 ${data.projects.length}개 캐싱 완료`);
                }
            })
            .catch(() => {});

        // ── NAS 프로젝트 동적 로딩 + fuse.js 인덱스 빌드 ──
        const loadNasProjects = () => {
            fetch('/api/schedule/nas-projects')
                .then(res => res.json())
                .then(data => {
                    if (data.success && data.projects?.length > 0) {
                        const scanned = data.projects.map((p: any) => ({
                            aliases: p.aliases || [],
                            nasId: p.nasId,
                            nasPrefix: p.nasPrefix,
                            fullName: p.fullName,
                        }));
                        dynamicNasProjects = mergeNasEntries(scanned, [...STATIC_NAS_FALLBACK, ...NAS_CONTEXT_OVERRIDES]);
                        buildFuseIndex(dynamicNasProjects);
                        nasProjectsLoadedAt = Date.now();
                        console.log(`[Scheduler] NAS 동적 프로젝트 ${data.projects.length}개 로딩 → fuse 인덱스 (총 ${dynamicNasProjects.length}개)`);
                    } else {
                        dynamicNasProjects = mergeNasEntries([], [...STATIC_NAS_FALLBACK, ...NAS_CONTEXT_OVERRIDES]);
                        buildFuseIndex(dynamicNasProjects);
                        nasProjectsLoadedAt = Date.now();
                        console.log('[Scheduler] NAS 스캔 실패 → 맥락+정적 폴백 fuse 인덱스');
                    }
                })
                .catch(() => {
                    dynamicNasProjects = mergeNasEntries([], [...STATIC_NAS_FALLBACK, ...NAS_CONTEXT_OVERRIDES]);
                    buildFuseIndex(dynamicNasProjects);
                    nasProjectsLoadedAt = Date.now();
                });
        };

        if (!nasProjectsLoadedAt || Date.now() - nasProjectsLoadedAt > NAS_PROJECTS_TTL_MS) {
            loadNasProjects();
        }

        // Load persisted audit log
        fetch('/api/schedule-audit')
            .then(res => res.json())
            .then(data => {
                if (data.entries && data.entries.length > 0) {
                    const typeMap: Record<string, NotifyLog['type']> = { add: 'add', delete: 'delete', modify: 'modify', move: 'move' };
                    const persistedLogs: NotifyLog[] = data.entries.map((e: any) => {
                        const diffMs = Date.now() - new Date(e.timestamp).getTime();
                        const diffMin = Math.floor(diffMs / 60000);
                        const ago = diffMin < 1 ? '방금 전' : diffMin < 60 ? `${diffMin}분 전` : diffMin < 1440 ? `${Math.floor(diffMin / 60)}시간 전` : `${Math.floor(diffMin / 1440)}일 전`;
                        return {
                            time: ago,
                            agent: e.agent || 'Meety',
                            msg: e.summary || '',
                            channels: e.channels || ['콘솔'],
                            type: typeMap[e.type] || 'system',
                        };
                    });
                    setNotifyHistory([...persistedLogs, ...INITIAL_HISTORY]);
                }
            })
            .catch(console.error);
    }, []);

    // ── 20초 폴링: 실시간 동기화 (경합 방지 강화) ──
    const isSavingRef = useRef(false);
    useEffect(() => {
        if (!mounted) return;
        const interval = setInterval(() => {
            // 저장 중이거나 최근 저장 후 15초 이내면 서버 데이터로 덮어쓰지 않음
            const timeSinceSync = Date.now() - lastSyncRef.current;
            if (isSavingRef.current || timeSinceSync < 15000) return;
            
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), 10000);
            
            fetch('/api/schedule', { signal: controller.signal })
                .then(res => res.json())
                .then(data => {
                    clearTimeout(timer);
                    // 폴링 응답 도착 시 다시 저장 중인지 확인 (이중 체크)
                    if (isSavingRef.current || (Date.now() - lastSyncRef.current) < 15000) return;
                    
                    if (data.tasks && Array.isArray(data.tasks)) {
                        setTasks(prev => {
                            // 데이터가 같으면 리렌더 방지 — ID+제목+날짜+상태 비교
                            const prevKey = prev.map(t => `${t.id}|${t.title}|${t.date}|${t.status}`).sort().join('\n');
                            const newKey = data.tasks.map((t: any) => `${t.id}|${t.title}|${t.date}|${t.status}`).sort().join('\n');
                            if (prevKey === newKey) return prev;
                            console.log('[Scheduler] 폴링 동기화: DB에서 변경 감지');
                            return data.tasks;
                        });
                    }
                    if (data.projects && Array.isArray(data.projects)) {
                        setProjects(data.projects);
                    }
                })
                .catch(() => { clearTimeout(timer); }); // 조용히 실패

            // ── 백그라운드에서 시스템 로그(에이전트 이력) 동기화 ──
            fetch('/api/schedule-audit')
                .then(res => res.json())
                .then(data => {
                    if (data.entries && data.entries.length > 0) {
                        const typeMap: Record<string, NotifyLog['type']> = { add: 'add', delete: 'delete', modify: 'modify', move: 'move' };
                        const persistedLogs: NotifyLog[] = data.entries.map((e: any) => {
                            const diffMs = Date.now() - new Date(e.timestamp).getTime();
                            const diffMin = Math.floor(diffMs / 60000);
                            const ago = diffMin < 1 ? '방금 전' : diffMin < 60 ? `${diffMin}분 전` : diffMin < 1440 ? `${Math.floor(diffMin / 60)}시간 전` : `${Math.floor(diffMin / 1440)}일 전`;
                            return {
                                time: ago,
                                agent: e.agent || 'Meety',
                                msg: e.summary || '',
                                channels: e.channels || ['콘솔'],
                                type: typeMap[e.type] || 'system',
                            };
                        });
                        setNotifyHistory([...persistedLogs, ...INITIAL_HISTORY]);
                    }
                })
                .catch(() => {});
                
        }, 20000); // 20초 — 경합 여유 확보
        return () => clearInterval(interval);
    }, [mounted]); // saveStatus 제거 — interval 재생성 방지

    const sendNotification = useCallback(async (changes: any) => {
        try {
            const res = await fetch('/api/notify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type: 'schedule_change',
                    changes,
                    agent: 'Meety',
                }),
            });
            const result = await res.json();
            const channels: string[] = [];
            if (result.channels?.telegram?.ok) channels.push('텔레그램');
            if (result.channels?.kakao?.ok) channels.push('카카오톡');

            const chText = channels.length > 0 ? channels.join(', ') : '콘솔 로그';
            setNotifyToast({ show: true, msg: `🤖 Meety: ${chText} 알림 발송 완료`, type: 'success' });
            setTimeout(() => setNotifyToast(p => ({ ...p, show: false })), 4000);
            return { channels, result };
        } catch (e) {
            setNotifyToast({ show: true, msg: '⚠️ 알림 발송 실패', type: 'error' });
            setTimeout(() => setNotifyToast(p => ({ ...p, show: false })), 4000);
            return { channels: [], result: null };
        }
    }, []);

    const addHistoryLog = useCallback((log: NotifyLog) => {
        setNotifyHistory(prev => [log, ...prev]);
    }, []);


    // 마지막 저장 시점 추적 — 폴링이 최근 저장을 덮어쓰지 않도록
    const lastSyncRef = useRef<number>(0);

    // ── 변경된 태스크만 서버에 전송 (전체 1500+건 전송 → 타임아웃 해결) ──
    const syncTasksToBackend = useCallback(async (changedTasks: Task[], deletedIds?: string[]) => {
        isSavingRef.current = true;
        lastSyncRef.current = Date.now();
        setSaveStatus('saving');
        try {
            const res = await fetch('/api/schedule', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    tasks: changedTasks,
                    deletedIds: deletedIds || [],
                }),
                signal: AbortSignal.timeout(10000),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(`HTTP ${res.status}: ${err.detail || ''}`);
            }
            lastSyncRef.current = Date.now();
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 2000);
            return true;
        } catch (error) {
            console.error('[Scheduler] 저장 실패:', error);
            setSaveStatus('idle');
            return false;
        } finally {
            setTimeout(() => { isSavingRef.current = false; }, 3000);
        }
    }, []);

    const handleUpdateTitle = async (taskId: string) => {
        if (!editValue.trim()) return setIsEditing(null);
        const originalTask = tasks.find(t => t.id === taskId);
        if (!originalTask) return setIsEditing(null);
        const changedTask = { ...originalTask, title: editValue };
        setTasks(prev => prev.map(t => t.id === taskId ? changedTask : t));
        setIsEditing(null);
        setEditMode(false);
        
        const success = await syncTasksToBackend([changedTask]);
        if (!success) {
            alert('데이터베이스 저장 중 오류가 발생했습니다.');
            setTasks(prev => prev.map(t => t.id === taskId ? originalTask : t));
            return;
        }

        if (originalTask.title !== editValue) {
            const changes = { modified: [{ title: editValue, date: originalTask.date, field: 'title', from: originalTask.title, to: editValue, projectId: originalTask.projectId, projectName: projects.find(p=>p.id===originalTask.projectId)?.name }] };
            sendNotification(changes).then(({ channels }) => {
                addHistoryLog({
                    time: '방금 전', agent: 'Meety',
                    msg: `일정 수정 감지 → "${originalTask.title.replace(/^[■*\s]+/, '')}" → "${editValue.replace(/^[■*\s]+/, '')}" (${formatDateKR(originalTask.date)})`,
                    channels: channels.length > 0 ? channels : ['콘솔'], type: 'modify'
                });
            });
        }
    };

    const handleStatusClickWithSync = async (taskId: string) => {
        const task = tasks.find(t => t.id === taskId);
        if (!task) return;
        const nextStatus: Record<TaskStatus, TaskStatus> = {
            'todo': 'done',
            'in-progress': 'done',
            'done': 'todo',
            'issue': 'todo'
        };
        const changedTask = { ...task, status: nextStatus[task.status] };
        setTasks(prev => prev.map(t => t.id === taskId ? changedTask : t));
        
        const success = await syncTasksToBackend([changedTask]);
        if (!success) {
            alert('상태 업데이트 중 오류가 발생했습니다.');
            setTasks(prev => prev.map(t => t.id === taskId ? task : t));
            return;
        }

        sendNotification({
            status: [{ title: task.title.replace(/^[■*\\s]+/, ''), date: task.date, status: changedTask.status, projectId: task.projectId, projectName: projects.find(p=>p.id===task.projectId)?.name }]
        }).then(({ channels }) => {
            addHistoryLog({ time: '방금 전', agent: 'Meety', msg: `일정 상태 변경 감지 → "${task.title.replace(/^[■*\\s]+/, '')}" ${changedTask.status === 'done' ? '완료' : changedTask.status}`, channels: channels.length > 0 ? channels : ['콘솔'], type: 'modify' });
        });
    };

    const handleAddTask = async (date: string) => {
        if (!newTaskTitle.trim()) { setShowAddModal(null); return; }
        
        let title = newTaskTitle.trim();
        const isDuplicate = tasks.some(t => t.title === title && t.date === date);
        if (isDuplicate) {
            alert('동일한 날짜에 같은 이름의 일정이 이미 존재합니다.');
            return;
        }
        let assignedProjectId = defaultProjectId || projects[0]?.id || 'default';
        let matchedProjectName = '';

        // 전역 스케줄러(defaultProjectId 없음)인 경우, 제목 기반 스마트 프로젝트 할당 진행
        if (!defaultProjectId) {
            // 1. '#키워드' 해시태그 명시적 매칭
            const hashtagMatch = title.match(/#([^\s]+)/);
            if (hashtagMatch) {
                const keyword = hashtagMatch[1];
                const matched = projects.find(p => p.name.includes(keyword) || p.id.includes(keyword));
                if (matched) {
                    assignedProjectId = matched.id;
                    matchedProjectName = matched.name;
                    title = title.replace(hashtagMatch[0], '').trim();
                }
            } 
            
            // 2. 제목 내 핵심 단어로 퍼지 매칭 시도 (해시태그 없을 때)
            if (!matchedProjectName) {
                const titleTokens = title.match(/[가-힣a-zA-Z0-9]{2,}/g) || [];
                titleTokens.sort((a, b) => b.length - a.length); // 긴 단어부터
                const ignoreWords = ['1차', '2차', '내역', '회의', '설계', '진행', '작업', '수정', '일정', '보고', '완료', '색채', '계획서', '색채계획서', '제출', '작성', '검토', '납품', '도면', '도서', '미팅', '현장', '출장', '협의', '테스트', '디자인', '견적', '시공', '자료', '확인'];
                
                for (const token of titleTokens) {
                    if (ignoreWords.includes(token)) continue;
                    // 이름매칭 (장성, 평택, 감매 등) — '일반 (' 형태도 제외
                    const matched = projects.find(p => p.name.includes(token) && !p.name.includes('일반 업무') && !p.name.includes('일반 ('));
                    if (matched) {
                        assignedProjectId = matched.id;
                        matchedProjectName = matched.name;
                        break;
                    }
                }
            }

            // 3. NAS alias 기반 스마트 매칭 (1·2에서 실패 시)
            if (!matchedProjectName) {
                const allEntries = dynamicNasProjects.length > 0
                    ? dynamicNasProjects
                    : mergeNasEntries([], [...STATIC_NAS_FALLBACK, ...NAS_CONTEXT_OVERRIDES]);
                const resolved = resolveNasProjectFromText(title, '', allEntries);
                const resolvedEntry = resolved?.entry ?? null;

                if (resolvedEntry) {
                    const existingProject = projects.find(p =>
                        p.name.includes(resolvedEntry!.nasPrefix) ||
                        resolvedEntry!.aliases.some((al: string) => p.name.includes(al)) ||
                        p.name.replace(/\s/g, '').includes(resolvedEntry!.fullName.replace(/\s/g, ''))
                    );
                    if (existingProject) {
                        assignedProjectId = existingProject.id;
                        matchedProjectName = existingProject.name;
                    } else {
                        const newProjectId = `proj-${resolvedEntry.nasPrefix}-${Date.now()}`;
                        const projectColors = [
                            'from-blue-500 to-cyan-400', 'from-purple-500 to-pink-500',
                            'from-orange-500 to-yellow-500', 'from-green-500 to-emerald-400',
                            'from-rose-500 to-red-500', 'from-teal-400 to-emerald-400',
                        ];
                        const newProject: Project = {
                            id: newProjectId,
                            name: resolvedEntry.fullName,
                            color: projectColors[projects.length % projectColors.length],
                        };
                        setProjects(prev => [...prev, newProject]);
                        assignedProjectId = newProjectId;
                        matchedProjectName = resolvedEntry.fullName;
                        await fetch('/api/schedule', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ newProject }),
                        }).catch(() => {});
                    }
                }
            }
        }

        const newTask: Task = {
            id: `task-${Date.now()}`,
            projectId: assignedProjectId,
            title,
            assignee: '',
            status: 'todo',
            date,
        };
        
        setTasks(prev => [...prev, newTask]);
        setNewTaskTitle('');
        setShowAddModal(null);
        
        const success = await syncTasksToBackend([newTask]);
        if (!success) {
            alert('데이터베이스 저장 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
            setTasks(prev => prev.filter(t => t.id !== newTask.id)); // 롤백
            return;
        }

        // 알림 메시지 스마트 생성 (DB 반영 성공 시에만 히스토리 추가)
        let msg = `📅 새 일정 추가 감지 → "${title}" (${formatDateKR(date)})`;
        if (matchedProjectName) {
            msg += `\n[💡 스마트 라우팅] → ${matchedProjectName}`;
        } else if (!defaultProjectId) {
            msg += `\n[⚠️ 자동 분류 실패] → 일반 업무로 배정됨 (연동 피드 없음)`;
        }

        const changes = { added: [{ title, date, projectId: assignedProjectId, projectName: matchedProjectName || projects.find(p=>p.id===assignedProjectId)?.name }] };
        sendNotification(changes).then(({ channels }) => {
            addHistoryLog({
                time: '방금 전', agent: 'Meety',
                msg,
                channels: channels.length > 0 ? channels : ['콘솔'], type: 'add'
            });
        });
    };

    const handleDeleteTask = async (taskId: string) => {
        const deletedTask = tasks.find(t => t.id === taskId);
        if (!deletedTask) return;
        setTasks(prev => prev.filter(t => t.id !== taskId));
        
        // 변경된 태스크 없음, 삭제만 전송
        const success = await syncTasksToBackend([], [taskId]);
        if (!success) {
            alert('일정 삭제 중 오류가 발생했습니다.');
            setTasks(prev => [...prev, deletedTask]); // 롤백
            return;
        }

        const changes = { deleted: [{ title: deletedTask.title, date: deletedTask.date, projectId: deletedTask.projectId, projectName: projects.find(p=>p.id===deletedTask.projectId)?.name }] };
        sendNotification(changes).then(({ channels }) => {
            addHistoryLog({
                time: '방금 전', agent: 'Meety',
                msg: `일정 삭제 감지 → "${deletedTask.title.replace(/^[■*\s]+/, '')}" (${formatDateKR(deletedTask.date)}) — 팀 알림 발송`,
                channels: channels.length > 0 ? channels : ['콘솔'], type: 'delete'
            });
        });
    };

    const handleDragStart = (taskId: string) => {
        setDraggedTask(taskId);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = async (e: React.DragEvent, targetDate: string) => {
        e.preventDefault();
        if (!draggedTask) return;
        const movedTask = tasks.find(t => t.id === draggedTask);
        const fromDate = movedTask?.date || '';
        if (fromDate === targetDate || !movedTask) { setDraggedTask(null); return; }

        const changedTask = { ...movedTask, date: targetDate };
        setTasks(prev => prev.map(t => t.id === draggedTask ? changedTask : t));
        setDraggedTask(null);
        
        const success = await syncTasksToBackend([changedTask]);
        if (!success) {
            alert('일정 이동 중 데이터베이스 에러가 발생했습니다.');
            setTasks(prev => prev.map(t => t.id === draggedTask ? movedTask : t)); // 롤백
            return;
        }

        const changes = { moved: [{ title: movedTask.title, fromDate, toDate: targetDate, projectId: movedTask.projectId, projectName: projects.find(p=>p.id===movedTask.projectId)?.name }] };
        sendNotification(changes).then(({ channels }) => {
            addHistoryLog({
                time: '방금 전', agent: 'Meety',
                msg: `일정 이동 감지 → "${movedTask.title.replace(/^[■*\s]+/, '')}" ${formatDateKR(fromDate)} → ${formatDateKR(targetDate)} — 팀 알림 발송`,
                channels: channels.length > 0 ? channels : ['콘솔'], type: 'move'
            });
        });
    };

    useEffect(() => {
        if (showAddModal && addInputRef.current) addInputRef.current.focus();
    }, [showAddModal]);

    const handleTaskContextMenu = useCallback((e: React.MouseEvent, task: Task) => {
        e.preventDefault();
        setTaskContextMenu({ x: e.clientX, y: e.clientY, task });
    }, []);

    const handleTaskSubtaskAdd = useCallback((e: React.MouseEvent, task: Task) => {
        e.preventDefault();
        setSubtaskAddTask({ x: e.clientX, y: e.clientY, task });
        setSubtaskTitle(`[하위] ${task.title.replace(/^[■*\\s]+/, '')} `);
        setSubtaskAssignee('');
    }, []);

    const toggleAssignee = useCallback((userId: string) => {
        if (!taskContextMenu?.task) return;
        const task = taskContextMenu.task;
        const assignees = task.assignees ? task.assignees.split(',').map((u) => u.trim()) : [];
        const newAssignees = assignees.includes(userId) ? assignees.filter((u) => u !== userId) : [...assignees, userId];
        const newAssigneesStr = newAssignees.join(', ');

        setTasks(prev => prev.map(t => t.id === task.id ? { ...t, assignees: newAssigneesStr } : t));
        setTaskContextMenu(prev => prev ? { ...prev, task: { ...prev.task, assignees: newAssigneesStr } } : null);

        fetch('/api/schedule/tasks', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: task.id, assignees: newAssigneesStr })
        })
        .then(() => {
            const added = !assignees.includes(userId);
            sendNotification({
                modify: [{ title: `[담당자 ${added ? '지정' : '해제'}] ${task.title.replace(/^[■*\\s]+/, '')} → ${userId}`, date: task.date, projectId: task.projectId, projectName: projects.find(p=>p.id===task.projectId)?.name }]
            });
        })
        .catch(console.error);
    }, [taskContextMenu, sendNotification]);

    const updateTaskMemo = useCallback((memo: string) => {
        if (!taskContextMenu?.task) return;
        const task = taskContextMenu.task;

        setTasks(prev => prev.map(t => t.id === task.id ? { ...t, memo } : t));
        setTaskContextMenu(prev => prev ? { ...prev, task: { ...prev.task, memo } } : null);

        fetch('/api/schedule/tasks', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: task.id, memo })
        }).catch(console.error);
    }, [taskContextMenu]);

    if (!mounted) return null;

    // Filter weeks: show only weeks that have tasks or are current week
    const visibleWeeks = WEEKS;

    const iconBtnIdle = isDark ? 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white' : 'bg-white border-gray-200 text-gray-500 hover:text-gray-900';
    const fullscreenButton = (
        <button
            onClick={toggleFullscreen}
            title={isFullscreen ? '전체화면 종료 (Esc)' : '캘린더 전체화면'}
            aria-label={isFullscreen ? '전체화면 종료' : '전체화면'}
            aria-pressed={isFullscreen}
            className={`p-2 rounded-xl border transition-all ${isFullscreen ? 'bg-indigo-500 border-indigo-400 text-white shadow-lg shadow-indigo-500/30' : iconBtnIdle}`}
        >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>
    );

    return (
        <div
            ref={rootRef}
            className={`w-full space-y-3 pb-16 lg:pb-0 ${isDark ? 'text-gray-200' : 'text-gray-800'} ${isFullscreen ? `overflow-y-auto p-3 sm:p-4 ${isDark ? 'bg-gray-950' : 'bg-gray-50'}` : ''} ${cssFullscreen ? 'fixed inset-0 z-[100]' : ''}`}
        >
            {/* Header Sequence */}
            {/* 한 줄 헤더: 제목 · 노드 헬스(접힘) | 아이콘 툴바 — 캘린더 지면 확보 */}
            <div className={`flex flex-wrap justify-between items-center gap-2 border-b pb-2 ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <div className="flex items-center gap-2 min-w-0">
                    <div className={`p-1.5 rounded-xl border ${isDark ? 'bg-indigo-500/10 border-indigo-500/20' : 'bg-indigo-50 border-indigo-200'}`}>
                        <Bot className="w-5 h-5 text-indigo-500 cursor-pointer hover:scale-110 shadow-sm transition-transform" onClick={() => setShowAgentChat(true)} />
                    </div>
                    <h1
                        className={`text-lg font-bold whitespace-nowrap ${isDark ? 'text-white' : 'text-gray-900'}`}
                        title="엑셀 기반 실시간 스케줄 · NAS 연동 · 메신저 자동 알림"
                    >
                        프로젝트 스케줄
                    </h1>
                    <NodeHealthWidget isDark={isDark} compact />
                </div>

                {/* ── 모바일 전용 툴바 (lg 미만) ── */}
                <div className="flex items-center gap-2 lg:hidden">
                    {editMode && (
                        <span className="text-[10px] font-bold text-amber-500 animate-pulse whitespace-nowrap">편집모드</span>
                    )}
                    {saveStatus === 'saving' && <span className="text-[10px] text-amber-500 animate-pulse">💾</span>}
                    {saveStatus === 'saved' && <span className="text-[10px] text-emerald-500">✅</span>}
                    <button
                        onClick={() => {
                            if (!isAuthenticated && !editMode) { alert('편집 모드는 인증된 사용자만 사용할 수 있습니다.'); return; }
                            setEditMode(!editMode);
                        }}
                        title={editMode ? '편집 완료' : '편집 모드'}
                        aria-pressed={editMode}
                        className={`p-2 rounded-xl border transition-all ${editMode
                            ? 'bg-gradient-to-r from-amber-500 to-orange-500 border-amber-400 text-white shadow-lg shadow-amber-500/30 animate-pulse'
                            : isDark
                                ? 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'
                                : 'bg-white border-gray-200 text-gray-500 hover:text-gray-900'
                        }`}
                    >
                        {editMode ? <Save className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                    </button>
                    {fullscreenButton}
                    <div className="relative">
                        <button
                            onClick={() => setShowToolbarMore(!showToolbarMore)}
                            onBlur={() => setTimeout(() => setShowToolbarMore(false), 200)}
                            title="더보기"
                            aria-label="더보기"
                            className={`p-2 rounded-xl border transition-all ${isDark ? 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white' : 'bg-white border-gray-200 text-gray-500 hover:text-gray-900'}`}
                        >
                            <MoreVertical className="w-4 h-4" />
                        </button>
                        {showToolbarMore && (
                            <div className={`absolute right-0 top-full mt-1 z-50 w-52 rounded-xl border shadow-xl overflow-hidden ${isDark ? 'bg-[#1a1d27] border-white/10' : 'bg-white border-gray-200'}`}>
                                <button onClick={async () => {
                                    setShowToolbarMore(false); setReportLoading(true);
                                    try {
                                        const res = await fetch('/api/report/weekly', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sendTelegram: true, sendKakao: true }) });
                                        const data = await res.json();
                                        if (data.success) { setReportData({ markdown: data.markdown, report: data.report }); setShowReport(true); }
                                        else setNotifyToast({ show: true, msg: '리포트 생성 실패: ' + (data.error || 'Unknown'), type: 'error' });
                                    } catch (e) { setNotifyToast({ show: true, msg: '리포트 생성 오류: ' + String(e), type: 'error' }); }
                                    setReportLoading(false);
                                }} disabled={reportLoading} className={`w-full px-4 py-2.5 text-left text-xs font-medium flex items-center gap-2 transition-colors disabled:opacity-50 ${isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-50 text-gray-600'}`}>
                                    <BrainCircuit className="w-3.5 h-3.5 text-blue-500" /> AI 리포트 생성
                                </button>
                                <label className={`w-full px-4 py-2.5 text-left text-xs font-medium flex items-center gap-2 cursor-pointer transition-colors ${isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-50 text-gray-600'}`}>
                                    <Save className="w-3.5 h-3.5 text-emerald-500" /> 엑셀 가져오기
                                    <input type="file" accept=".xlsx, .xls" className="hidden" onChange={async (e) => {
                                        const file = e.target.files?.[0]; if (!file) return;
                                        const reader = new FileReader();
                                        reader.onload = async (ev) => {
                                            const base64 = (ev.target?.result as string).split(',')[1];
                                            setSaveStatus('saving');
                                            try {
                                                const res = await fetch('/api/schedule/sync', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ excelBase64: base64 }) });
                                                if (res.ok) {
                                                    setNotifyToast({ show: true, msg: '엑셀 동기화 완료!', type: 'success' });
                                                    const refreshRes = await fetch('/api/schedule');
                                                    const data = await refreshRes.json();
                                                    if (data.projects) setProjects(data.projects);
                                                    if (data.tasks) { setTasks(data.tasks); }
                                                } else { setNotifyToast({ show: true, msg: '엑셀 동기화 실패', type: 'error' }); }
                                            } catch (err) { console.error(err); }
                                            setSaveStatus('idle');
                                        };
                                        reader.readAsDataURL(file);
                                    }} />
                                </label>
                                <button onClick={() => { window.location.href = '/api/schedule/export'; setShowToolbarMore(false); }} className={`w-full px-4 py-2.5 text-left text-xs font-medium flex items-center gap-2 transition-colors ${isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-50 text-gray-600'}`}>
                                    <HardDrive className="w-3.5 h-3.5 text-emerald-500" /> 엑셀 내보내기
                                </button>
                                <div className={`border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`} />
                                <button onClick={() => { setShowAnnouncement(!showAnnouncement); setShowToolbarMore(false); }} className={`w-full px-4 py-2.5 text-left text-xs font-medium flex items-center gap-2 transition-colors ${showAnnouncement ? (isDark ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-50 text-indigo-700') : (isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-50 text-gray-600')}`}>
                                    <Bell className="w-3.5 h-3.5" /> 공지 {showAnnouncement && '✓'}
                                </button>
                                <button onClick={() => { setShowDashboard(!showDashboard); setShowToolbarMore(false); }} className={`w-full px-4 py-2.5 text-left text-xs font-medium flex items-center gap-2 transition-colors ${showDashboard ? (isDark ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-50 text-emerald-700') : (isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-50 text-gray-600')}`}>
                                    <Activity className="w-3.5 h-3.5" /> 대시보드 {showDashboard && '✓'}
                                </button>
                                <div className={`border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`} />
                                <button onClick={() => setShowToolbarMore(false)} className={`w-full px-4 py-2.5 text-left text-xs font-medium flex items-center gap-2 transition-colors ${isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-50 text-gray-600'}`}>
                                    <MessageCircle className="w-3.5 h-3.5 text-yellow-500" /> 카카오톡 연동
                                </button>
                                <button onClick={() => { window.open('/flow', '_blank'); setShowToolbarMore(false); }} className={`w-full px-4 py-2.5 text-left text-xs font-medium flex items-center gap-2 transition-colors ${isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-50 text-gray-600'}`}>
                                    <Network className="w-3.5 h-3.5 text-emerald-500" /> 워크플로우 빌더
                                </button>
                                <button onClick={() => setShowToolbarMore(false)} className={`w-full px-4 py-2.5 text-left text-xs font-medium flex items-center gap-2 transition-colors ${isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-50 text-gray-600'}`}>
                                    <Send className="w-3.5 h-3.5 text-blue-500" /> 텔레그램 연동
                                </button>
                                <button onClick={() => {
                                    navigator.clipboard.writeText('https://desinsight.com/api/schedule/export/ical?token=snapgrid2026');
                                    setNotifyToast({ show: true, msg: '구글/노션/애플 캘린더 연동(iCal) 주소가 복사되었습니다!', type: 'success' });
                                    setShowToolbarMore(false);
                                }} className={`w-full px-4 py-2.5 text-left text-xs font-medium flex items-center gap-2 transition-colors ${isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-50 text-gray-600'}`}>
                                    <Calendar className="w-3.5 h-3.5 text-orange-400" /> 구글/노션/애플 캘린더 연동
                                </button>
                                <div className={`border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`} />
                                <button onClick={() => { setShowFlowEvents(!showFlowEvents); setShowToolbarMore(false); }} className={`w-full px-4 py-2.5 text-left text-xs font-medium flex items-center gap-2 transition-colors ${isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-50 text-gray-600'}`}>
                                    <EmojiIcon emoji="🔄" className="w-3.5 h-3.5" /> {showFlowEvents ? 'Flow 숨기기' : 'Flow 표시'} {showFlowEvents && '✓'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── 데스크톱 툴바 (lg 이상) ── */}
                <div className="hidden lg:flex flex-wrap items-center justify-end gap-1.5">
                    {saveStatus === 'saving' && <span className="text-xs text-amber-500 animate-pulse">💾 저장 중...</span>}
                    {saveStatus === 'saved' && <span className="text-xs text-emerald-500">✅ 저장 완료</span>}

                    {/* Primary actions: AI Report, Excel I/O */}
                    <button onClick={async () => {
                        setReportLoading(true);
                        try {
                            const res = await fetch('/api/report/weekly', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ sendTelegram: true, sendKakao: true }),
                            });
                            const data = await res.json();
                            if (data.success) {
                                setReportData({ markdown: data.markdown, report: data.report });
                                setShowReport(true);
                            } else {
                                setNotifyToast({ show: true, msg: '리포트 생성 실패: ' + (data.error || 'Unknown'), type: 'error' });
                            }
                        } catch (e) {
                            setNotifyToast({ show: true, msg: '리포트 생성 오류: ' + String(e), type: 'error' });
                        }
                        setReportLoading(false);
                    }} disabled={reportLoading} title={reportLoading ? 'AI 리포트 생성 중…' : 'AI 리포트 생성'} className="p-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 rounded-xl text-white shadow-lg shadow-blue-500/25 transition-all disabled:opacity-50">
                        {reportLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <BrainCircuit className="w-4 h-4" />}
                    </button>

                    <label title="엑셀 가져오기" className={`cursor-pointer p-2 border rounded-xl transition-all ${isDark ? 'bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/20 hover:border-emerald-500/40 text-emerald-500' : 'bg-white border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 text-emerald-600 shadow-sm'}`}>
                        <Save className="w-4 h-4" />
                        <input type="file" accept=".xlsx, .xls" className="hidden" onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = async (ev) => {
                                const base64 = (ev.target?.result as string).split(',')[1];
                                setSaveStatus('saving');
                                try {
                                    const res = await fetch('/api/schedule/sync', {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ excelBase64: base64 })
                                    });
                                    if (res.ok) {
                                        setNotifyToast({ show: true, msg: '엑셀 동기화 완료!', type: 'success' });
                                        // Refresh data
                                        const refreshRes = await fetch('/api/schedule');
                                        const data = await refreshRes.json();
                                        if (data.projects) setProjects(data.projects);
                                        if (data.tasks) {
                                            setTasks(data.tasks);
                                            sendNotification({ modified: [{ title: '엑셀 대량 동기화', date: todayStr }] }).then(({ channels }) => {
                                                addHistoryLog({
                                                    time: '방금 전', agent: 'NAS',
                                                    msg: `엑셀 파일 수동 업로드에 의한 스케줄 대량 동기화 완료`,
                                                    channels: channels.length > 0 ? channels : ['콘솔'], type: 'system'
                                                });
                                            });
                                        }
                                    } else {
                                        setNotifyToast({ show: true, msg: '엑셀 동기화 실패', type: 'error' });
                                    }
                                } catch (err) {
                                    console.error(err);
                                }
                                setSaveStatus('idle');
                            };
                            reader.readAsDataURL(file);
                        }} />
                    </label>
                    <button onClick={() => {
                        window.location.href = '/api/schedule/export';
                    }} title="엑셀 내보내기" className={`p-2 border rounded-xl transition-all ${isDark ? 'bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/20 hover:border-emerald-500/40 text-emerald-500' : 'bg-white border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 text-emerald-600 shadow-sm'}`}>
                        <HardDrive className="w-4 h-4" />
                    </button>

                    <div className={`w-px h-6 mx-1 ${isDark ? 'bg-white/10' : 'bg-gray-200'}`} />
                    {/* Edit Mode Toggle */}
                    <button
                        onClick={() => {
                            if (!isAuthenticated && !editMode) {
                                setNotifyToast({ show: true, msg: '편집 모드는 인증된 사용자만 사용할 수 있습니다.', type: 'error' });
                                return;
                            }
                            setEditMode(!editMode);
                        }}
                        title={editMode ? '편집 완료 (저장)' : '편집 모드'}
                        aria-pressed={editMode}
                        className={`p-2 border rounded-xl transition-all ${editMode
                            ? 'bg-gradient-to-r from-amber-500 to-orange-500 border-amber-400 text-white shadow-lg shadow-amber-500/30 animate-pulse'
                            : isDark
                                ? 'bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/20 hover:border-emerald-500/40 text-gray-200'
                                : 'bg-white border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 text-gray-700 shadow-sm'
                            }`}
                    >
                        {editMode ? <Save className="w-4 h-4" /> : <Pencil className="w-4 h-4 text-emerald-500" />}
                    </button>
                    {fullscreenButton}

                    {/* 더보기 드롭다운 — 공지/대시보드/연동/Flow 토글 */}
                    <div className="relative">
                        <button
                            onClick={() => setShowToolbarMore(!showToolbarMore)}
                            onBlur={() => setTimeout(() => setShowToolbarMore(false), 200)}
                            title="더보기 (공지·대시보드·연동·Flow)"
                            className={`p-2 border rounded-xl transition-all ${isDark ? 'bg-gray-800 border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-500' : 'bg-gray-50 border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                        >
                            <MoreVertical className="w-4 h-4" />
                        </button>
                        {showToolbarMore && (
                            <div className={`absolute right-0 top-full mt-1 z-50 w-52 rounded-xl border shadow-xl overflow-hidden ${isDark ? 'bg-[#1a1d27] border-white/10' : 'bg-white border-gray-200'}`}>
                                <button onClick={() => { setShowAnnouncement(!showAnnouncement); setShowToolbarMore(false); }} className={`w-full px-4 py-2.5 text-left text-xs font-medium flex items-center gap-2 transition-colors ${showAnnouncement ? (isDark ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-50 text-indigo-700') : (isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-50 text-gray-600')}`}>
                                    <Bell className="w-3.5 h-3.5" /> 공지 {showAnnouncement && '✓'}
                                </button>
                                <button onClick={() => { setShowDashboard(!showDashboard); setShowToolbarMore(false); }} className={`w-full px-4 py-2.5 text-left text-xs font-medium flex items-center gap-2 transition-colors ${showDashboard ? (isDark ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-50 text-emerald-700') : (isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-50 text-gray-600')}`}>
                                    <Activity className="w-3.5 h-3.5" /> 대시보드 {showDashboard && '✓'}
                                </button>
                                <div className={`border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`} />
                                <button onClick={() => setShowToolbarMore(false)} className={`w-full px-4 py-2.5 text-left text-xs font-medium flex items-center gap-2 transition-colors ${isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-50 text-gray-600'}`}>
                                    <MessageCircle className="w-3.5 h-3.5 text-yellow-500" /> 카카오톡 연동
                                </button>
                                <button onClick={() => { window.open('/flow', '_blank'); setShowToolbarMore(false); }} className={`w-full px-4 py-2.5 text-left text-xs font-medium flex items-center gap-2 transition-colors ${isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-50 text-gray-600'}`}>
                                    <Network className="w-3.5 h-3.5 text-emerald-500" /> 워크플로우 빌더
                                </button>
                                <button onClick={() => setShowToolbarMore(false)} className={`w-full px-4 py-2.5 text-left text-xs font-medium flex items-center gap-2 transition-colors ${isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-50 text-gray-600'}`}>
                                    <Send className="w-3.5 h-3.5 text-blue-500" /> 텔레그램 연동
                                </button>
                                <button onClick={() => {
                                    navigator.clipboard.writeText('https://desinsight.com/api/schedule/export/ical?token=snapgrid2026');
                                    setNotifyToast({ show: true, msg: '구글/노션/애플 캘린더 연동(iCal) 주소가 복사되었습니다!', type: 'success' });
                                    setShowToolbarMore(false);
                                }} className={`w-full px-4 py-2.5 text-left text-xs font-medium flex items-center gap-2 transition-colors ${isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-50 text-gray-600'}`}>
                                    <Calendar className="w-3.5 h-3.5 text-orange-400" /> 구글/노션/애플 캘린더 연동
                                </button>
                                <div className={`border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`} />
                                <button onClick={() => { setShowFlowEvents(!showFlowEvents); setShowToolbarMore(false); }} className={`w-full px-4 py-2.5 text-left text-xs font-medium flex items-center gap-2 transition-colors ${isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-50 text-gray-600'}`}>
                                    <EmojiIcon emoji="🔄" className="w-3.5 h-3.5" /> {showFlowEvents ? 'Flow 숨기기' : 'Flow 표시'} {showFlowEvents && '✓'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Announcement Banner — 토글 */}
            {showAnnouncement && (
                <div className={`mt-2 rounded-xl p-4 flex items-start justify-between gap-3 border transition-colors ${isDark ? 'bg-indigo-500/10 border-indigo-500/20 text-indigo-200' : 'bg-indigo-50 border-indigo-200 text-indigo-800'}`}>
                    <div className="flex items-start gap-3">
                        <div className="mt-0.5 text-xl"><EmojiIcon emoji="📢" className="w-5 h-5" /></div>
                        <div className="flex-1">
                            <h3 className="text-sm font-bold flex items-center gap-2">
                                [전사 공지] 오늘부터 전체 임직원 대상 «스케줄러» 및 «플로우» 정식 사용을 시작합니다! 🚀
                            </h3>
                            <p className={`text-xs mt-1.5 leading-relaxed ${isDark ? 'text-indigo-300' : 'text-indigo-600'}`}>
                                이제부터 업무 분장(담당자 지정) 및 세부 일정 변동 사항이 <b>Co-Work Flow 폴더</b>에 자동 연동됩니다.<br/>
                                <b>🔔 실시간 스케줄 변경 알림 수신 방법:</b><br/>
                                👉 텔레그램 스냅그리드봇(<span className="font-mono bg-black/10 px-1 py-0.5 rounded mx-0.5 select-all">@desinsight_Poby_bot</span>) 채팅창에 <strong className="font-mono text-[13px] bg-black/20 px-1.5 py-0.5 rounded ml-0.5 select-all">/시작</strong> 을 입력하시면 즉시 알림 서비스를 받아보실 수 있습니다.
                            </p>
                        </div>
                    </div>
                    <button onClick={() => setShowAnnouncement(false)} className={`shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-lg transition-colors hover:bg-black/5 dark:hover:bg-white/10`} title="이 공지 닫기">✕</button>
                </div>
            )}

            {/* KPI Cards — 대시보드 토글 */}
            {showDashboard && (
            <div className="flex overflow-x-auto snap-x snap-mandatory gap-2 scrollbar-hide md:grid md:grid-cols-2 lg:grid-cols-4 md:overflow-visible">
                <div className={`border rounded-xl px-4 py-3 flex items-center gap-3 group snap-start min-w-[70vw] sm:min-w-[180px] md:min-w-0 ${isDark ? 'bg-gray-900 border-white/5' : 'bg-white border-gray-200 shadow-sm'}`}>
                    <Activity className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <div>
                        <div className={`text-lg font-bold leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>94<span className={`text-xs font-normal ml-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>%</span></div>
                        <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>일정 준수 · +2%</p>
                    </div>
                </div>
                <div className={`border rounded-xl px-4 py-3 flex items-center gap-3 group snap-start min-w-[70vw] sm:min-w-[180px] md:min-w-0 ${isDark ? 'bg-gray-900 border-white/5' : 'bg-white border-gray-200 shadow-sm'}`}>
                    <AlertTriangle className="w-5 h-5 text-rose-500 flex-shrink-0" />
                    <div>
                        <div className={`text-lg font-bold leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>3<span className={`text-xs font-normal ml-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>건</span></div>
                        <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>지연/이슈</p>
                    </div>
                </div>
                <div className={`border rounded-xl px-4 py-3 flex items-center gap-3 group snap-start min-w-[70vw] sm:min-w-[180px] md:min-w-0 ${isDark ? 'bg-gray-900 border-white/5' : 'bg-white border-gray-200 shadow-sm'}`}>
                    <Plane className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <div>
                        <div className={`text-lg font-bold leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>8<span className={`text-xs font-normal ml-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>건</span></div>
                        <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>출장/경영보고</p>
                    </div>
                </div>
                <div className={`border rounded-xl px-4 py-3 flex items-center gap-3 group snap-start min-w-[70vw] sm:min-w-[180px] md:min-w-0 ${isDark ? 'bg-gray-900 border-purple-500/20' : 'bg-purple-50 border-purple-200'}`}>
                    <HardDrive className="w-5 h-5 text-purple-500 flex-shrink-0" />
                    <div>
                        <div className={`text-lg font-bold leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>Sync<span className="text-xs text-emerald-500 font-normal ml-1">Live</span></div>
                        <p className={`text-[10px] ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>엑셀 동기화</p>
                    </div>
                </div>
            </div>
            )}

            {/* Tabs — 뷰 전환 + 기능 진입 시각 분리 */}
            <div className={`flex border-b overflow-x-auto scrollbar-hide ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                {/* 뷰 전환 탭 */}
                <div className="flex gap-1 sm:gap-4 lg:gap-6">
                    <button
                        onClick={() => setActiveTab('scheduler')}
                        className={`pb-3 px-2 sm:px-0 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors border-b-2 ${activeTab === 'scheduler' ? 'border-indigo-500 text-indigo-500' : `border-transparent ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}`}
                    >
                        <span className="hidden sm:inline">실시간 </span>스케줄러
                    </button>
                    {!defaultProjectId && (
                        <button
                            onClick={() => setActiveTab('history')}
                            className={`pb-3 px-2 sm:px-0 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors border-b-2 flex items-center gap-1 sm:gap-2 ${activeTab === 'history' ? 'border-indigo-500 text-indigo-500' : `border-transparent ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}`}
                        >
                            <span className="hidden sm:inline">에이전트 </span>히스토리
                            <span className="bg-rose-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">New</span>
                        </button>
                    )}
                    <button
                        onClick={() => setActiveTab('logs')}
                        className={`pb-3 px-2 sm:px-0 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors border-b-2 flex items-center gap-1 sm:gap-2 ${activeTab === 'logs' ? 'border-emerald-500 text-emerald-500' : `border-transparent ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}`}
                    >
                        <Activity className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">실시간 </span>로그
                        {(liveLogStats?.total || liveLogs.length) > 0 && (
                            <span className="bg-emerald-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                                {liveLogStats?.total || liveLogs.length}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('gantt')}
                        className={`hidden md:flex pb-3 px-2 sm:px-0 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors border-b-2 items-center gap-1 ${activeTab === 'gantt' ? 'border-indigo-500 text-indigo-500' : `border-transparent ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}`}
                    >
                        📊 간트
                    </button>
                    <button
                        onClick={() => setActiveTab('briefing')}
                        className={`pb-3 px-2 sm:px-0 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors border-b-2 flex items-center gap-1 ${activeTab === 'briefing' ? 'border-indigo-500 text-indigo-500' : `border-transparent ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}`}
                    >
                        📋 브리핑
                    </button>
                </div>

                {/* 구분선 (데스크톱만) */}
                <div className={`hidden md:block w-px self-stretch my-2 ${isDark ? 'bg-white/10' : 'bg-gray-200'}`} />

                {/* 기능/도구 탭 (데스크톱만) */}
                <div className="hidden md:flex gap-1 sm:gap-4 lg:gap-6">
                    <button
                        onClick={() => setActiveTab('analytics')}
                        className={`pb-3 px-2 sm:px-0 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors border-b-2 ${activeTab === 'analytics' ? 'border-purple-500 text-purple-500' : `border-transparent ${isDark ? 'text-gray-600 hover:text-gray-300' : 'text-gray-400 hover:text-gray-700'}`}`}
                    >
                        RAG<span className="hidden sm:inline"> 심층 분석</span>
                    </button>
                    <a
                        href="/flow"
                        className={`pb-3 px-2 sm:px-0 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors border-b-2 border-transparent flex items-center gap-1 ${isDark ? 'text-gray-600 hover:text-gray-300' : 'text-gray-400 hover:text-gray-700'}`}
                    >
                        <Network className="w-3.5 h-3.5" /> 플로우
                    </a>
                    <button
                        onClick={() => { setActiveTab('backup'); if (!backupStatus) { fetch('/api/backup').then(r => r.json()).then(setBackupStatus).catch(console.error); } }}
                        className={`pb-3 px-2 sm:px-0 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors border-b-2 flex items-center gap-1 ${activeTab === 'backup' ? 'border-purple-500 text-purple-500' : `border-transparent ${isDark ? 'text-gray-600 hover:text-gray-300' : 'text-gray-400 hover:text-gray-700'}`}`}
                    >
                        🗄️ 백업
                    </button>
                    <button
                        onClick={() => setActiveTab('coding')}
                        className={`pb-3 px-2 sm:px-0 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors border-b-2 flex items-center gap-1 ${activeTab === 'coding' ? 'border-purple-500 text-purple-500' : `border-transparent ${isDark ? 'text-gray-600 hover:text-gray-300' : 'text-gray-400 hover:text-gray-700'}`}`}
                    >
                    👨🏻‍💻 코딩
                </button>
                    <button
                        onClick={() => setActiveTab('training')}
                        className={`pb-3 px-2 sm:px-0 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors border-b-2 flex items-center gap-1 ${activeTab === 'training' ? 'border-emerald-500 text-emerald-500' : `border-transparent ${isDark ? 'text-gray-600 hover:text-gray-300' : 'text-gray-400 hover:text-gray-700'}`}`}
                    >
                        ??
                    </button>
                {/* 그룹챗 바로가기 */}
                <a href="/chat/group"
                  className="hidden md:flex pb-3 px-2 font-bold text-xs sm:text-sm whitespace-nowrap transition-colors border-b-2 border-transparent items-center gap-1 rounded-t"
                  style={{ padding: '3px 8px', gap: '4px', color: '#10b981', background: '#10b98115' }}
                >
                  💬 채팅
                </a>
                {/* 에이전트 바로가기 - 구분선 */}
                <div className="hidden md:flex items-center px-1" style={{ padding: '0 3px' }}>
                  <div style={{ width: 1, height: 20, background: 'rgba(128,128,128,0.3)', flexShrink: 0 }} />
                </div>
                <div className="hidden md:flex items-center" style={{ gap: 0 }}>
                  {[
                    { id: 'poby', name: 'Poby', color: '#ff9a3d' },
                    { id: 'jessi', name: 'Jessi', color: '#ec4899' },
                    { id: 'shon', name: 'Shon', color: '#ff4d4d' },
                    { id: 'benny', name: 'Benny', color: '#3b82f6' },
                    { id: 'dony', name: 'Dony', color: '#10b981' },
                    { id: 'meety', name: 'Meety', color: '#a855f7' },
                    { id: 'tony', name: 'Tony', color: '#eab308' },
                  ].map(a => (
                    <a key={a.id}
                      href="https://t.me/c/4425102661"
                      target="_blank" rel="noopener noreferrer"
                      title={`${a.name} — SnapGRID 단톡방(Telegram)으로 이동`}
                      className="pb-3 px-1 sm:px-1.5 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors border-b-2 border-transparent flex items-center gap-1"
                      style={{ padding: '3px 4px', gap: '2px' }}
                      onMouseEnter={e => { e.currentTarget.style.background = a.color + '22'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: a.color, display: 'inline-block', flexShrink: 0 }} />
                      <span>{a.name}</span>
                    </a>
                  ))}
                </div>
                </div>
            </div>

            {/* Main Content Area */}

            <AnimatePresence mode="wait">
                {activeTab === 'scheduler' && (
                    <DndSchedulerProvider
                        isDark={isDark}
                        editMode={editMode}
                        tasks={tasks}
                        onTaskMove={async (taskId, fromDate, toDate) => {
                            const movedTask = tasks.find(t => t.id === taskId);
                            if (!movedTask || fromDate === toDate) return;
                            // 클라이언트 최종 가드 — 완료 태스크 차단
                            if (movedTask.status === 'done') {
                                console.warn('[Scheduler] 완료된 태스크 이동 차단:', movedTask.title);
                                return;
                            }
                            const targetDayTasks = tasks.filter(t => t.date === toDate);
                            const maxOrder = targetDayTasks.length > 0 ? Math.max(...targetDayTasks.map(t => typeof t.sortOrder === 'number' ? t.sortOrder : 0)) : -1;
                            const changedTask = { ...movedTask, date: toDate, sortOrder: maxOrder + 1 };
                            // 낙관적 업데이트
                            const prevTasks = tasks;
                            setTasks(prev => prev.map(t => t.id === taskId ? changedTask : t));
                            // PUT 단일 업데이트 (서버 측 status 재검증 + audit)
                            try {
                                const res = await fetch('/api/schedule', {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ taskId, date: toDate }),
                                    signal: AbortSignal.timeout(8000),
                                });
                                if (!res.ok) {
                                    const err = await res.json().catch(() => ({}));
                                    // 서버 거부 → 롤백
                                    setTasks(prevTasks);
                                    console.error('[Scheduler] 이동 거부:', err);
                                    if (typeof window !== 'undefined') {
                                        setNotifyToast({ show: true, msg: err.error || '이동에 실패했습니다. 다시 시도해주세요.', type: 'error' });
                                    }
                                    return;
                                }
                                // sortOrder 는 POST bulk 로 별도 동기화 (기존 동작 유지)
                                syncTasksToBackend([changedTask]);
                                sendNotification({ moved: [{ title: movedTask.title, fromDate, toDate }] })
                                    .then(({ channels }) => {
                                        addHistoryLog({ time: '방금 전', agent: 'Meety', msg: `일정 이동 → "${movedTask.title.replace(/^[■*\s]+/, '')}" ${formatDateKR(fromDate)} → ${formatDateKR(toDate)}`, channels: channels.length > 0 ? channels : ['콘솔'], type: 'move' });
                                    })
                                    .catch(e => console.error('Notification error on move:', e));
                                refreshAudit();
                            } catch (e) {
                                // 네트워크/타임아웃 → 롤백
                                setTasks(prevTasks);
                                console.error('[Scheduler] 이동 네트워크 오류:', e);
                                if (typeof window !== 'undefined') {
                                    setNotifyToast({ show: true, msg: '이동 중 네트워크 오류. 변경이 취소되었습니다.', type: 'error' });
                                }
                            }
                        }}
                        onReorder={(allTasks, dateReorderedTasks) => {
                            setTasks(allTasks);
                            if (dateReorderedTasks && dateReorderedTasks.length > 0) {
                                syncTasksToBackend(dateReorderedTasks);
                            }
                        }}
                    >
                    <motion.div
                        key="sched"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className={`rounded-2xl overflow-hidden transition-all ${editMode ? 'ring-2 ring-amber-500/50' : ''} ${isDark ? 'bg-gray-900 border border-white/10 shadow-2xl' : 'bg-white border border-gray-200 shadow-xl'}`}
                    >
                        {/* Edit Mode Banner */}
                        {editMode && (
                            <div className="px-4 py-1.5 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-b border-amber-500/30 flex items-center gap-2">
                                <Zap className="w-3.5 h-3.5 text-amber-500" />
                                <span className="text-xs font-bold text-amber-400">편집 모드 활성 — 미완료 태스크만 드래그로 이동, 클릭하여 수정</span>
                            </div>
                        )}
                        {/* Week Navigation Bar */}
                        {/* Week Navigation — 월/연도 바로 이동 포함 */}
                        <div className={`px-2 sm:px-4 py-2 flex items-center justify-between border-b ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-gray-50/50'}`}>
                            <button
                                onClick={() => setWeekOffset(o => o - 1)}
                                className={`p-1.5 sm:px-3 sm:py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-0.5 sm:gap-1 ${isDark ? 'bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white border border-white/10' : 'bg-white hover:bg-gray-100 text-gray-600 hover:text-gray-900 border border-gray-200 shadow-sm'}`}
                            >
                                <ChevronLeft className="w-4 h-4 sm:w-3.5 sm:h-3.5" /><span className="hidden sm:inline"> 이전 주</span>
                            </button>
                            <div className="flex items-center gap-1.5 sm:gap-2 relative">
                                <button
                                    onClick={() => { setShowMonthPicker(v => !v); if (!showMonthPicker && WEEKS.length > 0) setPickerYear(parseInt(WEEKS[0][0].date.split('-')[0])); }}
                                    className={`flex items-center gap-1.5 px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm font-bold transition-all cursor-pointer ${isDark ? 'text-gray-200 hover:bg-white/10' : 'text-gray-800 hover:bg-gray-100'}`}
                                    title="월/연도 바로 이동"
                                >
                                    <Calendar className="w-3.5 h-3.5 text-indigo-500" />
                                    {weekRangeLabel}
                                </button>
                                <button
                                    onClick={() => setWeekOffset(0)}
                                    className={`px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-lg text-[10px] sm:text-[11px] font-bold shadow-sm hover:shadow-md transition-all ${weekOffset === 0 ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white' : 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30'}`}
                                >
                                    오늘
                                </button>
                                <span className={`text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'} hidden sm:inline`}>{todayStr}</span>
                                {/* Month/Year Quick Jump Popover */}
                                {showMonthPicker && (
                                    <>
                                        <div className="fixed inset-0 z-40" onClick={() => setShowMonthPicker(false)} />
                                        <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50 rounded-2xl shadow-2xl border p-3 sm:p-4 w-[260px] sm:w-[300px] ${isDark ? 'bg-gray-900 border-white/15 shadow-black/50' : 'bg-white border-gray-200 shadow-gray-300/50'}`}>
                                            {/* Year Navigation */}
                                            <div className="flex items-center justify-between mb-3">
                                                <button
                                                    onClick={() => setPickerYear(y => y - 1)}
                                                    className={`p-1.5 rounded-lg transition-all ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                                                >
                                                    <ChevronLeft className="w-4 h-4" />
                                                </button>
                                                <span className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{pickerYear}년</span>
                                                <button
                                                    onClick={() => setPickerYear(y => y + 1)}
                                                    className={`p-1.5 rounded-lg transition-all ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                                                >
                                                    <ChevronRight className="w-4 h-4" />
                                                </button>
                                            </div>
                                            {/* Month Grid */}
                                            <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
                                                {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => {
                                                    const now = new Date();
                                                    const isCurrentMonth = pickerYear === now.getFullYear() && month === now.getMonth() + 1;
                                                    return (
                                                        <button
                                                            key={month}
                                                            onClick={() => {
                                                                // 선택한 달의 1일이 포함된 주로 이동
                                                                const targetDate = new Date(pickerYear, month - 1, 1);
                                                                const today = new Date();
                                                                const todayStart = new Date(today);
                                                                todayStart.setDate(today.getDate() - today.getDay());
                                                                todayStart.setHours(0, 0, 0, 0);
                                                                const targetStart = new Date(targetDate);
                                                                targetStart.setDate(targetDate.getDate() - targetDate.getDay());
                                                                targetStart.setHours(0, 0, 0, 0);
                                                                const diffWeeks = Math.round((targetStart.getTime() - todayStart.getTime()) / (7 * 86400000));
                                                                setWeekOffset(diffWeeks);
                                                                setShowMonthPicker(false);
                                                            }}
                                                            className={`py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
                                                                isCurrentMonth
                                                                    ? 'bg-gradient-to-r from-indigo-500 to-blue-500 text-white shadow-lg shadow-indigo-500/30 scale-105'
                                                                    : isDark
                                                                        ? 'hover:bg-white/10 text-gray-300 hover:text-white'
                                                                        : 'hover:bg-indigo-50 text-gray-700 hover:text-indigo-600'
                                                            }`}
                                                        >
                                                            {month}월
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            {/* Quick shortcuts */}
                                            <div className={`mt-3 pt-3 flex justify-center gap-2 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                                                <button
                                                    onClick={() => { setWeekOffset(0); setShowMonthPicker(false); }}
                                                    className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-sm hover:shadow-md transition-all"
                                                >
                                                    📍 오늘로 이동
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                            <button
                                onClick={() => setWeekOffset(o => o + 1)}
                                className={`p-1.5 sm:px-3 sm:py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-0.5 sm:gap-1 ${isDark ? 'bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white border border-white/10' : 'bg-white hover:bg-gray-100 text-gray-600 hover:text-gray-900 border border-gray-200 shadow-sm'}`}
                            >
                                <span className="hidden sm:inline">다음 주 </span><ChevronRight className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                            </button>
                        </div>
                        {/* Legend row */}
                        {/* Compact Legend + Edit Mode Indicator */}
                        {/* Legend — 모바일 가로 스크롤 */}
                        <div className={`px-2 sm:px-3 py-1.5 sm:py-2 flex items-center gap-1.5 sm:gap-2 text-[9px] sm:text-[10px] font-medium border-b overflow-x-auto scrollbar-hide ${isDark ? 'border-white/10 bg-white/[0.02] text-gray-400' : 'border-gray-200 bg-gray-50/80 text-gray-600'}`}>
                            {editMode && (
                                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-500 font-bold text-[10px] animate-pulse whitespace-nowrap">
                                    <Pencil className="w-2.5 h-2.5" /> 편집 모드
                                </span>
                            )}
                            {PROJECT_LEGENDS.map((leg) => (
                                <button
                                    key={leg.label}
                                    onClick={() => setLegendFilter(prev => prev === leg.label ? null : leg.label)}
                                    className={`flex items-center gap-0.5 sm:gap-1 whitespace-nowrap px-1.5 py-0.5 rounded transition-all ${legendFilter === leg.label ? (isDark ? 'bg-white/10 ring-1 ring-white/20' : 'bg-gray-200 ring-1 ring-gray-400') : 'hover:bg-white/5'}`}
                                >
                                    <div className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-sm flex-shrink-0 ${leg.color}`}></div>
                                    <span>{leg.label}</span>
                                </button>
                            ))}
                            {legendFilter && (
                                <button onClick={() => setLegendFilter(null)} className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'}`}>
                                    필터 해제
                                </button>
                            )}
                        </div>

                        {/* Desktop Grid View (hidden on mobile) */}
                        <div 
                            className="overflow-x-auto relative scrollbar-hide hidden md:block"
                            onWheel={handleCalendarWheel}
                            onContextMenu={(e) => { e.preventDefault(); setEditMode(prev => !prev); }}
                        >
                            <div className={`min-w-[900px] flex flex-col text-[12px] ${isDark ? 'text-gray-300' : 'text-gray-800'}`}>
                                {/* Global Header Row — No sidebar column */}
                                <div className="grid border-b" style={{ gridTemplateColumns: '0.6fr repeat(5, 1fr) 0.6fr' }}>
                                    {['일', '월', '화', '수', '목', '금', '토'].map((name, i) => (
                                        <div key={name} className={`p-2 font-bold text-center overflow-hidden text-ellipsis whitespace-nowrap ${i < 6 ? 'border-r' : ''} ${i === 0 ? 'text-red-500' : i === 6 ? 'text-blue-500' : ''} ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>{name}</div>
                                    ))}
                                </div>

                                {/* Week Blocks */}
                                {/* Week Blocks — no sidebar column */}
                                {visibleWeeks.map((week, wIdx) => {
                                    const hasToday = week.some(d => d.date === todayStr);
                                    return (
                                        <div key={wIdx} className={`flex flex-col border-b last:border-b-0 transition-shadow ${hasToday ? (isDark ? 'ring-2 ring-indigo-500 shadow-[0_0_20px_rgba(99,102,241,0.3)] z-10 rounded-lg' : 'ring-2 ring-blue-500 shadow-[0_0_20px_rgba(59,130,246,0.3)] z-10 rounded-lg') : (isDark ? 'border-gray-700' : 'border-gray-300')}`}>
                                            {/* Week Dates Row */}
                                            <div className={`grid border-b ${isDark ? 'border-gray-700 bg-indigo-900/10' : 'border-gray-300 bg-blue-50/40'}`} style={{ gridTemplateColumns: '0.6fr repeat(5, 1fr) 0.6fr' }}>
                                                {week.map((d, i) => (
                                                    <div key={d.date} className={`relative p-1.5 ${i < 6 ? 'border-r' : ''} flex items-center gap-1.5 overflow-hidden min-w-0 ${d.isSun ? 'text-red-500 font-bold' : d.isSat ? 'text-blue-500 font-bold' : 'font-medium'} ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
                                                        <span className="text-[12px]">{d.display}</span>
                                                        {d.date === todayStr && <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded font-bold ${isDark ? 'bg-indigo-500/30 text-indigo-300' : 'bg-indigo-100 text-indigo-600'}`}>Today</span>}
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Task Row — full width 7 columns (DnD enabled) */}
                                            <div className={`grid transition-colors ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50'}`} style={{ gridTemplateColumns: '0.6fr repeat(5, 1fr) 0.6fr' }}>
                                                {week.map((d, i) => {
                                                    const allDayTasks = tasks.filter(t => t.date === d.date);
                                                    const LEGEND_KEYWORDS: Record<string, string[]> = {
                                                        '제안서': ['제안', 'PT', '프레젠'],
                                                        '설계도서': ['도서', '설계'],
                                                        '내역': ['내역', '원가'],
                                                        '경영지원': [],
                                                        '회의': ['회의', '취합', '협의', '자문'],
                                                        '계약정산': ['정산', '계약', '청구'],
                                                        '견적제출': ['견적'],
                                                        '업무연락': ['보고', '연락'],
                                                        '동영상': ['CG', '동영상', '렌더'],
                                                        '도면제출': ['도면', '디자인'],
                                                        '납품제출': ['제출', '납품', '업로드'],
                                                    };
                                                    const dayTasks = legendFilter
                                                        ? allDayTasks.filter(t => {
                                                            const kws = LEGEND_KEYWORDS[legendFilter] || [];
                                                            const pn = projects.find(p => p.id === t.projectId)?.name || '';
                                                            if (legendFilter === '경영지원') return pn.includes('경영');
                                                            return kws.some(kw => t.title.includes(kw));
                                                        })
                                                        : allDayTasks;
                                                    const dayFlowEvents = showFlowEvents ? (flowEvents[d.date] || []) : [];
                                                    return (
                                                        <div
                                                            key={d.date}
                                                            className={`relative min-h-[90px] p-2 ${i < 6 ? 'border-r' : ''} flex flex-col gap-1.5 min-w-0 overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-300'} ${d.date === todayStr ? (isDark ? 'bg-indigo-500/10 shadow-inner' : 'bg-blue-50/50 shadow-inner') : ''}`}
                                                        >
                                                            {d.date === todayStr && (
                                                                <>
                                                                    <style>{`
                                                                        @keyframes slowBreathingPulse {
                                                                            0%, 100% { 
                                                                                opacity: 0.3; 
                                                                                box-shadow: 0 0 0 rgba(99,102,241,0);
                                                                            }
                                                                            50% { 
                                                                                opacity: 1; 
                                                                                box-shadow: 0 0 15px rgba(99,102,241,0.5), inset 0 0 10px rgba(99,102,241,0.2);
                                                                            }
                                                                        }
                                                                        .slow-pulse-border {
                                                                            animation: slowBreathingPulse 3s ease-in-out infinite;
                                                                        }
                                                                    `}</style>
                                                                    <div className={`absolute inset-0 z-10 pointer-events-none rounded border-2 ${isDark ? 'border-indigo-500' : 'border-blue-500'} slow-pulse-border`} />
                                                                    <div className={`absolute inset-0 z-0 pointer-events-none rounded border-2 ${isDark ? 'border-indigo-500/20' : 'border-blue-500/20'}`} />
                                                                </>
                                                            )}
                                                            <DroppableDayColumn
                                                                isDark={isDark}
                                                                editMode={editMode}
                                                                onEditModeToggle={() => setEditMode(true)}
                                                                dateStr={d.date}
                                                                isToday={d.date === todayStr}
                                                                tasks={dayTasks}
                                                                getLegendColorClass={getLegendColorClass}
                                                                getProjectName={(pid) => projects.find(p => p.id === pid)?.name || ''}
                                                                onTaskClick={(task) => {
                                                                    const pn = projects.find(p => p.id === task.projectId)?.name || '';
                                                                    setHistoryModalTask({ id: task.id, title: task.title.replace(/^[■*\s]+/, ''), projectId: task.projectId, date: d.date, projectName: pn });
                                                                }}
                                                                onTaskDoubleClick={(task) => { setIsEditing(task.id); setEditValue(task.title); }}
                                                                onTaskStatusToggle={handleStatusClickWithSync}
                                                                onTaskDelete={handleDeleteTask}
                                                                onTaskContextMenu={handleTaskContextMenu}
                                                                onTaskAssigneeManage={handleTaskContextMenu}
                                                                onTaskSubtaskAdd={handleTaskSubtaskAdd}
                                                                editingTaskId={isEditing}
                                                                editValue={editValue}
                                                                onEditValueChange={setEditValue}
                                                                onEditSubmit={handleUpdateTitle}
                                                                onEditCancel={() => setIsEditing(null)}
                                                            />
                                                            {/* Flow Events — 클릭 시 플로우 프로젝트 대시보드로 이동 */}
                                                            {dayFlowEvents.map((fe) => (
                                                                <div key={`flow-${fe.id}`}
                                                                    className="flex items-start text-[11px] leading-[1.3] p-1 -m-1 rounded cursor-pointer hover:bg-purple-500/10 transition-colors"
                                                                    title={`[Flow] ${fe.projectName || ''} — ${fe.title} (클릭하여 대시보드 이동)`}
                                                                    onClick={() => {
                                                                        if (fe.projectId) {
                                                                            router.push(`/flow/project/${fe.projectId}`);
                                                                        } else if (fe.projectName) {
                                                                            navigateToFlowProject(fe.projectName);
                                                                        } else {
                                                                            router.push('/flow');
                                                                        }
                                                                    }}
                                                                >
                                                                    <span className="w-2.5 h-2.5 mt-[2.5px] mr-1.5 flex-shrink-0 rounded-full" style={{ background: fe.color || '#7c3aed' }} />
                                                                    <span className="break-words font-medium" style={{ color: isDark ? '#c4b5fd' : '#7c3aed' }}>
                                                                        {fe.title.replace(/^[■*\s]+/, '')}
                                                                    </span>
                                                                    {fe.status === 'done' && <span className="ml-1 text-[9px] text-emerald-500">✓</span>}
                                                                </div>
                                                            ))}
                                                            {/* Add Task Button */}
                                                            {editMode && (
                                                                showAddModal === d.date ? (
                                                                    <div className="mt-1">
                                                                        <input
                                                                            ref={showAddModal === d.date ? addInputRef : undefined}
                                                                            value={newTaskTitle}
                                                                            onChange={e => setNewTaskTitle(e.target.value)}
                                                                            onKeyDown={e => {
                                                                                if (e.key === 'Enter') handleAddTask(d.date);
                                                                                if (e.key === 'Escape') { setShowAddModal(null); setNewTaskTitle(''); }
                                                                            }}
                                                                            onBlur={() => { if (!newTaskTitle.trim()) { setShowAddModal(null); setNewTaskTitle(''); } }}
                                                                            placeholder="새 일정 입력..."
                                                                            className={`w-full text-[11px] px-1.5 py-1 rounded border outline-none ${isDark ? 'bg-gray-800 border-amber-500/40 text-white placeholder:text-gray-500' : 'bg-white border-amber-500/40 text-black placeholder:text-gray-400'}`}
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <button
                                                                        onClick={() => { setShowAddModal(d.date); setNewTaskTitle(''); }}
                                                                        className={`mt-1 w-full flex items-center justify-center gap-1 rounded py-0.5 text-[10px] font-medium transition-all opacity-0 group-hover:opacity-100 hover:opacity-100 ${isDark ? 'hover:bg-emerald-500/10 text-emerald-500/60 hover:text-emerald-400' : 'hover:bg-emerald-50 text-emerald-400 hover:text-emerald-600'}`}
                                                                    >
                                                                        <Plus className="w-3 h-3" /> 추가
                                                                    </button>
                                                                )
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Mobile List View (visible only on mobile < md) */}
                        <div
                            className="md:hidden"
                            onTouchStart={handleTouchStart}
                            onTouchEnd={handleTouchEnd}
                            onContextMenu={(e) => { e.preventDefault(); setEditMode(prev => !prev); }}
                        >
                            {visibleWeeks.map((week, wIdx) => {
                                const weekStart = week[0];
                                const weekEnd = week[6];
                                const hasToday = week.some(d => d.date === todayStr);
                                const weekTasks = week.reduce((sum, d) => sum + tasks.filter(t => t.date === d.date).length, 0);
                                return (
                                    <div key={wIdx}>
                                        {/* 주 헤더 */}
                                        <div className={`sticky top-0 z-20 px-3 py-2 flex items-center justify-between ${isDark ? 'bg-gray-950 border-gray-800' : 'bg-white border-gray-200'} border-b`}>
                                            <span className={`text-xs font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                {weekStart.display}({weekStart.dayName}) ~ {weekEnd.display}({weekEnd.dayName})
                                            </span>
                                            <div className="flex items-center gap-2">
                                                {weekTasks > 0 && <span className="text-[10px] text-indigo-500 font-bold">{weekTasks}건</span>}
                                                {hasToday && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-500 text-white font-bold">이번 주</span>}
                                            </div>
                                        </div>
                                        {week.map((d) => {
                                            const dayTasks = tasks.filter(t => t.date === d.date);
                                            const dayFlowEvents = (showFlowEvents && flowEvents[d.date]) || [];
                                            const isAddingHere = showAddModal === d.date;
                                            const hasContent = dayTasks.length > 0 || dayFlowEvents.length > 0;
                                            return (
                                                <div key={d.date} className={`border-b ${isDark ? 'border-gray-800/50' : 'border-gray-100'}`}>
                                                    <div className={`px-3 py-2 flex items-center gap-2 ${d.date === todayStr ? (isDark ? 'bg-indigo-500/10' : 'bg-indigo-50') : ''} ${d.isSun ? 'text-red-400' : d.isSat ? 'text-blue-400' : ''}`}>
                                                        <span className="text-sm font-bold">{d.display}</span>
                                                        <span className={`text-[11px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{d.dayName}</span>
                                                        {d.date === todayStr && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-500 text-white font-bold">TODAY</span>}
                                                        <span className={`ml-auto text-[10px] ${isDark ? 'text-gray-700' : 'text-gray-300'}`}>{dayTasks.length > 0 ? `${dayTasks.length}건` : ''}</span>
                                                    </div>
                                                    {(hasContent || isAddingHere) && (
                                                        <div className="px-3 pb-2 space-y-1">
                                                            {dayTasks.map((t, idx) => {
                                                                const projectName = projects.find(p => p.id === t.projectId)?.name || '';
                                                                const displayTitle = t.title.replace(/^[■*\s]+/, '');
                                                                return (
                                                                    <div key={t.id + '-m-' + idx} className={`flex items-center flex-wrap gap-2 py-2 px-2.5 rounded-xl text-sm active:scale-[0.98] transition-transform ${isDark ? 'hover:bg-white/5 active:bg-white/10' : 'hover:bg-gray-50 active:bg-gray-100'}`}
                                                                        onClick={!editMode ? () => handleStatusClickWithSync(t.id) : () => { setIsEditing(t.id); setEditValue(t.title); }}
                                                                        onContextMenu={(e) => handleTaskContextMenu(e, t)}
                                                                    >
                                                                        {isEditing === t.id ? (
                                                                            <input
                                                                                autoFocus
                                                                                value={editValue}
                                                                                onChange={e => setEditValue(e.target.value)}
                                                                                onBlur={() => handleUpdateTitle(t.id)}
                                                                                onKeyDown={e => {
                                                                                    if (e.key === 'Enter') handleUpdateTitle(t.id);
                                                                                    if (e.key === 'Escape') setIsEditing(null);
                                                                                }}
                                                                                className={`flex-1 min-w-0 bg-transparent border-b-2 outline-none text-sm font-medium py-0.5 ${isDark ? 'border-amber-500 text-white' : 'border-amber-500 text-black'}`}
                                                                            />
                                                                        ) : (
                                                                            <>
                                                                                <span
                                                                                    className={`w-3 h-3 rounded-sm flex-shrink-0 cursor-pointer hover:scale-125 hover:ring-2 hover:ring-offset-1 hover:ring-indigo-400 transition-all ${getLegendColorClass(t.title, projectName)}`}
                                                                                    onClick={(e) => { e.stopPropagation(); handleStatusClickWithSync(t.id); }}
                                                                                    title="클릭하여 완료 상태 토글"
                                                                                ></span>
                                                                                <div className="flex-1 min-w-0">
                                                                                    <span className={`block truncate ${t.status === 'done' ? 'line-through opacity-40' : ''} ${displayTitle.includes('휴일') ? 'text-red-500 font-bold' : ''}`}>
                                                                                        {displayTitle}
                                                                                    </span>
                                                                                    {projectName && <span className={`text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{projectName}</span>}
                                                                                </div>
                                                                                {t.status === 'issue' && <AlertTriangle className="w-4 h-4 text-rose-500 animate-pulse flex-shrink-0" />}
                                                                                {t.status === 'done' && <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                                                                                {editMode && (
                                                                                    <>
                                                                                        <button onClick={(e) => { e.stopPropagation(); setIsEditing(t.id); setEditValue(t.title); }} className="ml-1 opacity-100 text-amber-500 hover:text-amber-400 flex-shrink-0" title="수정"><Pencil className="w-4 h-4" /></button>
                                                                                        <button onClick={(e) => { e.stopPropagation(); handleDeleteTask(t.id); }} className="ml-1 opacity-100 text-rose-500 hover:text-rose-400 flex-shrink-0" title="삭제"><X className="w-4 h-4" /></button>
                                                                                    </>
                                                                                )}
                                                                                {t.assignees && (
                                                                                    <div className="w-full mt-1 flex flex-wrap gap-1 border-t border-transparent pl-5">
                                                                                        {t.assignees.split(',').map((u, i) => u.trim() ? (
                                                                                            <span key={i} className={`px-1 py-[1px] rounded text-[9px] font-bold ${isDark ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>{u.trim()}</span>
                                                                                        ) : null)}
                                                                                    </div>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                            {dayFlowEvents.map((fe) => (
                                                                <div key={`flow-${fe.id}`} className="flex items-start text-[11px] leading-[1.3] p-1 -m-1 rounded cursor-pointer hover:bg-purple-500/10 transition-colors" title={`[Flow] ${fe.projectName || ''} — ${fe.title}`} onClick={() => { if (fe.projectId) router.push(`/flow/project/${fe.projectId}`); }}>
                                                                    <span className="w-2.5 h-2.5 mt-[2.5px] mr-1.5 flex-shrink-0 rounded-full" style={{ background: fe.color || '#7c3aed' }} />
                                                                    <span className="break-words font-medium" style={{ color: isDark ? '#c4b5fd' : '#7c3aed' }}>{fe.title.replace(/^[■*\s]+/, '')}</span>
                                                                    {fe.status === 'done' && <span className="ml-1 text-[9px] text-emerald-500">✓</span>}
                                                                </div>
                                                            ))}
                                                            {isAddingHere ? (
                                                                <div className="py-2">
                                                                    <input autoFocus value={newTaskTitle} onChange={e => setNewTaskTitle(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleAddTask(d.date); if (e.key === 'Escape') { setShowAddModal(null); setNewTaskTitle(''); } }} onBlur={() => { if (!newTaskTitle.trim()) { setShowAddModal(null); setNewTaskTitle(''); } }} placeholder="새 일정 입력..." className={`w-full text-sm px-3 py-2 rounded-lg border outline-none ${isDark ? 'bg-gray-800 border-emerald-500/40 text-white placeholder:text-gray-500' : 'bg-white border-emerald-500/40 text-black placeholder:text-gray-400'}`} />
                                                                </div>
                                                            ) : editMode && (
                                                                <button onClick={() => { setShowAddModal(d.date); setNewTaskTitle(''); }} className={`w-full py-1.5 rounded-lg text-xs font-medium flex items-center justify-center gap-1 ${isDark ? 'text-emerald-400 hover:bg-emerald-500/10' : 'text-emerald-600 hover:bg-emerald-50'}`}>
                                                                    <Plus className="w-3 h-3" /> 일정 추가
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>
                    </motion.div>
                    {/* Audit History Panel */}
                    <div className={`mt-2 rounded-xl overflow-hidden transition-all ${isDark ? 'bg-gray-900/80 border border-white/10' : 'bg-white/90 border border-gray-200 shadow-sm'}`}>
                        <button
                            onClick={() => setShowAudit(v => !v)}
                            className={`w-full px-4 py-2.5 flex items-center justify-between text-xs font-semibold transition-colors ${isDark ? 'hover:bg-white/5 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-50 text-gray-500 hover:text-gray-800'}`}
                        >
                            <span className="flex items-center gap-2">
                                <History className="w-3.5 h-3.5 text-indigo-500" />
                                이력
                                {auditLogs.length > 0 && (
                                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isDark ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}>
                                        {auditLogs.length}
                                    </span>
                                )}
                            </span>
                            <span className="transition-transform" style={{ transform: showAudit ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                <ChevronDown className="w-3.5 h-3.5" />
                            </span>
                        </button>

                        <AnimatePresence>
                            {showAudit && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="overflow-hidden"
                                >
                                    <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                                        {auditLoading && auditLogs.length === 0 ? (
                                            <div className={`px-4 py-6 text-center text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>불러오는 중...</div>
                                        ) : auditLogs.length === 0 ? (
                                            <div className={`px-4 py-6 text-center text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>이력이 없습니다</div>
                                        ) : (
                                            <div className="max-h-[300px] overflow-y-auto divide-y divide-solid">
                                                {auditLogs.map((log: any) => {
                                                    const isRejected = log.type === 'schedule_move_rejected';
                                                    const isMove = log.type === 'schedule_task_move';
                                                    const ts = log.timestamp ? new Date(log.timestamp) : null;
                                                    const timeStr = ts
                                                        ? `${ts.getMonth() + 1}/${ts.getDate()} ${String(ts.getHours()).padStart(2, '0')}:${String(ts.getMinutes()).padStart(2, '0')}`
                                                        : '-';
                                                    let badgeCls = isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500';
                                                    let badgeLabel = '업데이트';
                                                    if (isRejected) { badgeCls = 'bg-red-500/15 text-red-400'; badgeLabel = '이동 거부'; }
                                                    else if (isMove) { badgeCls = 'bg-blue-500/15 text-blue-400'; badgeLabel = '일정 이동'; }

                                                    return (
                                                        <div key={log.id} className={`px-4 py-2 flex items-start gap-3 text-xs ${isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-gray-50'}`}>
                                                            <span className={`flex-shrink-0 w-14 text-right tabular-nums ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{timeStr}</span>
                                                            <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold ${badgeCls}`}>{badgeLabel}</span>
                                                            <div className="flex-1 min-w-0">
                                                                <span className={`font-medium truncate block ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{log.summary || '-'}</span>
                                                                {(log.details?.oldValue || log.details?.newValue) && (
                                                                    <span className={`block mt-0.5 truncate ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                                                                        {log.details.oldValue && <>{log.details.oldValue}</>}
                                                                        {log.details.oldValue && log.details.newValue && <span className="mx-1">→</span>}
                                                                        {log.details.newValue && <>{log.details.newValue}</>}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                    </DndSchedulerProvider>
                )}

                {!defaultProjectId && activeTab === 'history' && (
                    <motion.div
                        key="hist"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className={`rounded-2xl p-6 ${isDark ? 'bg-gray-900 border border-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}
                    >
                        <h3 className={`text-lg font-medium mb-6 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            <MessageSquare className="w-5 h-5 text-indigo-500" />
                            통합 에이전트 커뮤니케이션 보드
                            {notifyHistory.length > INITIAL_HISTORY.length && (
                                <span className="bg-rose-500 text-white text-[10px] px-2 py-0.5 rounded-full animate-pulse">
                                    {notifyHistory.length - INITIAL_HISTORY.length}건 새 알림
                                </span>
                            )}
                        </h3>
                        <div className="space-y-4">
                            {(() => {
                                const grouped: Array<{ log: NotifyLog; count: number }> = [];
                                for (const log of notifyHistory) {
                                    const prev = grouped.length > 0 ? grouped[grouped.length - 1] : null;
                                    if (prev && prev.log.agent === log.agent && prev.log.msg === log.msg) {
                                        prev.count++;
                                    } else {
                                        grouped.push({ log, count: 1 });
                                    }
                                }
                                return grouped.map(({ log, count }, i) => {
                                const isNew = log.time === '방금 전';
                                const typeColors: Record<string, string> = {
                                    add: 'from-emerald-500 to-green-600',
                                    delete: 'from-rose-500 to-red-600',
                                    modify: 'from-amber-500 to-orange-600',
                                    move: 'from-blue-500 to-cyan-600',
                                    system: 'from-indigo-500 to-purple-600',
                                };
                                const typeLabels: Record<string, string> = {
                                    add: '📌 추가', delete: '🗑️ 삭제', modify: '✏️ 수정', move: '📅 이동', system: '🔗 시스템',
                                };
                                return (
                                    <motion.div
                                        key={i}
                                        initial={isNew ? { opacity: 0, x: -20 } : false}
                                        animate={{ opacity: 1, x: 0 }}
                                        className={`flex gap-4 p-4 rounded-xl border transition-colors ${isNew ? (isDark ? 'border-amber-500/30 bg-amber-500/5' : 'border-amber-300 bg-amber-50') : (isDark ? 'border-white/5 bg-white/[0.01] hover:bg-white/[0.03]' : 'border-gray-200 bg-gray-50 hover:bg-gray-100')}`}
                                    >
                                        <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${typeColors[log.type] || typeColors.system} flex items-center justify-center shrink-0 shadow-lg`}>
                                            <span className="font-bold text-white text-sm">{log.agent[0]}</span>
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                <span className={`font-bold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{log.agent}</span>
                                                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{log.time}</span>
                                                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${isNew ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' : 'bg-blue-500/10 text-blue-500 border-blue-500/20'}`}>
                                                    {typeLabels[log.type] || '채널 연동'}
                                                </span>
                                                {log.channels.map((ch, ci) => (
                                                    <span key={ci} className={`text-[10px] px-1.5 py-0.5 rounded-full ${ch === '텔레그램' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' :
                                                        ch === '카카오톡' ? 'bg-yellow-500/10 text-yellow-600 border border-yellow-500/20' :
                                                            'bg-gray-500/10 text-gray-400 border border-gray-500/20'
                                                        }`}>
                                                        {ch === '텔레그램' ? '✈️' : ch === '카카오톡' ? '💬' : '📋'} {ch}
                                                    </span>
                                                ))}
                                            </div>
                                            <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{log.msg}{count > 1 && <span className={`ml-2 text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}> (동일 메시지 {count}회)</span>}</p>
                                        </div>
                                        {isNew && <Zap className="w-4 h-4 text-amber-500 animate-pulse flex-shrink-0 mt-1" />}
                                    </motion.div>
                                );
                                });
                            })()}
                        </div>
                    </motion.div>
                )}

                {activeTab === 'logs' && (
                    <motion.div
                        key="live-logs"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className={`rounded-2xl overflow-hidden border ${isDark ? 'bg-gray-950 border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}
                    >
                        <div className={`px-5 py-4 border-b flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-gray-50'}`}>
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
                                    <Activity className="w-5 h-5 text-emerald-400" />
                                </div>
                                <div className="min-w-0">
                                    <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>실시간 업무 로그 보드</h3>
                                    <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                        Prisma AuditLog 기반 · 5초 자동 갱신 · 에이전트 대체 후보 이벤트 수집
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                    최신 갱신 {formatLiveLogTime(liveLogUpdatedAt)}
                                </span>
                                <button
                                    onClick={() => refreshLiveLogs()}
                                    disabled={liveLogLoading}
                                    className={`h-9 px-3 rounded-lg text-xs font-medium border flex items-center gap-2 transition-colors disabled:opacity-60 ${isDark ? 'bg-white/5 border-white/10 text-gray-200 hover:bg-white/10' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                                >
                                    <RefreshCw className={`w-3.5 h-3.5 ${liveLogLoading ? 'animate-spin' : ''}`} />
                                    새로고침
                                </button>
                            </div>
                        </div>

                        <div className="p-5 space-y-5">
                            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                                {[
                                    { label: '전체', value: liveLogStats?.total || liveLogs.length, kind: 'system' as LiveLogKind },
                                    { label: '일정', value: liveLogStats?.schedule || 0, kind: 'schedule' as LiveLogKind },
                                    { label: 'NAS', value: liveLogStats?.nas || 0, kind: 'nas' as LiveLogKind },
                                    { label: '에이전트', value: liveLogStats?.agent || 0, kind: 'agent' as LiveLogKind },
                                    { label: '파이프라인', value: liveLogStats?.pipeline || 0, kind: 'pipeline' as LiveLogKind },
                                    { label: '시스템', value: liveLogStats?.system || 0, kind: 'system' as LiveLogKind },
                                ].map(item => (
                                    <div key={item.label} className={`rounded-xl border p-3 ${isDark ? 'bg-white/[0.02] border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                                        <div className={`text-[11px] mb-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{item.label}</div>
                                        <div className={`text-xl font-bold ${LIVE_LOG_KIND_STYLES[item.kind].split(' ')[1]}`}>{item.value}</div>
                                    </div>
                                ))}
                            </div>

                            <div className="flex items-center gap-2 overflow-x-auto pb-1">
                                {LIVE_LOG_FILTERS.map(filter => (
                                    <button
                                        key={filter.id}
                                        onClick={() => setLiveLogFilter(filter.id)}
                                        className={`px-3 py-1.5 rounded-full text-xs font-medium border whitespace-nowrap transition-colors ${liveLogFilter === filter.id
                                            ? 'bg-emerald-500 text-white border-emerald-500'
                                            : isDark ? 'bg-white/5 border-white/10 text-gray-400 hover:text-gray-200' : 'bg-white border-gray-200 text-gray-600 hover:text-gray-900'
                                            }`}
                                    >
                                        {filter.label}
                                    </button>
                                ))}
                            </div>

                            {liveLogWatchRoots.length > 0 && (
                                <div className={`rounded-xl border p-3 ${isDark ? 'bg-white/[0.02] border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                                    <div className={`text-[11px] mb-2 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>관찰 루트</div>
                                    <div className="flex gap-2 overflow-x-auto pb-1">
                                        {liveLogWatchRoots.map(root => (
                                            <div key={root.id} className={`shrink-0 max-w-[220px] rounded-lg border px-3 py-2 ${root.kind === 'shared-source' ? 'border-amber-500/20 bg-amber-500/10' : isDark ? 'border-white/10 bg-white/[0.03]' : 'border-gray-200 bg-white'}`}>
                                                <div className={`text-xs font-semibold truncate ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{root.owner}</div>
                                                <div className={`text-[10px] truncate ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{root.nasPath}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="space-y-3">
                                {liveLogLoading && liveLogs.length === 0 && (
                                    <div className={`rounded-xl border p-8 text-center ${isDark ? 'border-white/10 text-gray-500' : 'border-gray-200 text-gray-500'}`}>
                                        실시간 로그를 불러오는 중입니다.
                                    </div>
                                )}

                                {!liveLogLoading && liveLogs.length === 0 && (
                                    <div className={`rounded-xl border p-8 text-center ${isDark ? 'border-white/10 text-gray-500' : 'border-gray-200 text-gray-500'}`}>
                                        아직 표시할 로그가 없습니다. 일정 변경, NAS 이벤트, 에이전트 작업이 저장되면 이곳에 누적됩니다.
                                    </div>
                                )}

                                {liveLogs.map(log => (
                                    <motion.div
                                        key={log.id}
                                        layout
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className={`rounded-xl border p-4 ${isDark ? 'bg-white/[0.02] border-white/10 hover:bg-white/[0.04]' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                                    >
                                        <div className="flex gap-3">
                                            <div className={`mt-0.5 w-9 h-9 rounded-lg border flex items-center justify-center shrink-0 ${LIVE_LOG_KIND_STYLES[log.kind]}`}>
                                                {log.kind === 'nas' ? <HardDrive className="w-4 h-4" /> :
                                                    log.kind === 'agent' ? <Bot className="w-4 h-4" /> :
                                                        log.kind === 'pipeline' ? <BrainCircuit className="w-4 h-4" /> :
                                                            log.kind === 'schedule' ? <Calendar className="w-4 h-4" /> :
                                                                <AlertTriangle className="w-4 h-4" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${LIVE_LOG_KIND_STYLES[log.kind]}`}>
                                                        {LIVE_LOG_KIND_LABELS[log.kind]}
                                                    </span>
                                                    <span className={`text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{formatLiveLogTime(log.timestamp)}</span>
                                                    <span className={`text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>by {log.agent}</span>
                                                    <span className={`text-[11px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{log.channel}</span>
                                                    {log.owner && (
                                                        <span className="text-[10px] px-2 py-0.5 rounded-full border bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                                                            담당 {log.owner}
                                                        </span>
                                                    )}
                                                    {log.workType && (
                                                        <span className="text-[10px] px-2 py-0.5 rounded-full border bg-violet-500/10 text-violet-400 border-violet-500/20">
                                                            {LIVE_LOG_WORK_TYPE_LABELS[log.workType] || log.workType}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className={`font-semibold truncate ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                                                    {log.title || log.action}
                                                </div>
                                                {(log.summary || log.projectId || log.filePath || log.taskId || log.watchRoot) && (
                                                    <div className={`mt-2 text-xs leading-relaxed space-y-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                        {log.summary && <p className="break-words">{log.summary}</p>}
                                                        <div className="flex gap-2 flex-wrap">
                                                            {log.projectId && <span className="break-all">Project: {log.projectId}</span>}
                                                            {log.taskId && <span className="break-all">Task: {log.taskId}</span>}
                                                            {log.watchRoot && <span className="break-all">Root: {log.watchRoot.label}</span>}
                                                        </div>
                                                        {log.filePath && <p className="break-all">Path: {log.filePath}</p>}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}

                {activeTab === 'analytics' && <RAGAnalyticsPanel isDark={isDark} tasks={tasks} projects={projects} />}
            </AnimatePresence>

            {/* Notification Toast — outside AnimatePresence to prevent duplicate key errors */}
            <AnimatePresence>
                {notifyToast.show && (
                    <motion.div
                        key="notify-toast"
                        initial={{ opacity: 0, y: -20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20, scale: 0.95 }}
                        className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-3 backdrop-blur-xl border ${notifyToast.type === 'success'
                            ? 'bg-emerald-500/90 border-emerald-400/50 text-white'
                            : 'bg-rose-500/90 border-rose-400/50 text-white'
                            }`}
                    >
                        <Bell className="w-5 h-5 animate-bounce" />
                        <span className="font-medium text-sm">{notifyToast.msg}</span>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── 간트차트 탭 ── */}
            {activeTab === 'gantt' && (
                <motion.div key="gantt" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-4">
                    <GanttChart isDark={isDark} />
                </motion.div>
            )}



            {/* ── AI 일일 브리핑 ── */}
            {activeTab === 'briefing' && (
                <motion.div key="briefing" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
                    {/* Header */}
                    <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-gray-900 border-white/10' : 'bg-white border-gray-200 shadow-xl'}`}>
                        <div className={`px-6 py-4 border-b flex items-center justify-between ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-gray-50'}`}>
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-bold text-sm"><EmojiIcon emoji="��" className="w-4 h-4 text-inherit inline-block" /></div>
                                <div>
                                    <h3 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>프로젝트 인텔리전스</h3>
                                    <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>전사 업무 현황 · {new Date().toLocaleDateString('ko-KR')}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setIsBriefingHistoryOpen(true)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all flex items-center gap-1 ${isDark ? 'bg-white/5 border-white/10 hover:bg-white/10 text-gray-300' : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-700'}`}
                                >
                                    <History className="w-3.5 h-3.5" />
                                    히스토리
                                </button>
                                <button
                                    onClick={() => {
                                        setBriefingLoading(true);
                                        setBriefingData(null);
                                        Promise.all([
                                            fetch('/api/schedule/daily-briefing?format=markdown').then(r => r.json()),
                                            fetch('/api/schedule/project-insights?range=week').then(r => r.json()),
                                        ]).then(([briefing, insights]) => {
                                            setBriefingData({ ...briefing, insights: insights.success ? insights : null });
                                            setBriefingLoading(false);
                                        }).catch(() => setBriefingLoading(false));
                                    }}
                                    className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-lg transition-all"
                                >
                                    <EmojiIcon emoji="🔄" className="w-3.5 h-3.5 inline mb-0.5" /> 새로고침
                                </button>
                            </div>
                        </div>

                        {briefingLoading ? (
                            <div className="flex items-center justify-center py-20 gap-3">
                                <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
                                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>인텔리전스 데이터 수집 중...</span>
                            </div>
                        ) : (
                            <div className="p-5 space-y-5">
                                {/* ── KPI 카드 ── */}
                                <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
                                    {[
                                        { label: '진행중', value: briefingData?.insights?.summary?.inProgress || 0, color: 'text-blue-500', bg: 'from-blue-500/10 to-blue-600/5' },
                                        { label: '잔여', value: briefingData?.insights?.summary?.todo || 0, color: 'text-amber-500', bg: 'from-amber-500/10 to-amber-600/5' },
                                        { label: '완료', value: briefingData?.insights?.summary?.done || 0, color: 'text-emerald-500', bg: 'from-emerald-500/10 to-emerald-600/5' },
                                        { label: '이슈', value: briefingData?.insights?.summary?.issue || 0, color: 'text-rose-500', bg: 'from-rose-500/10 to-rose-600/5' },
                                        { label: '지연', value: briefingData?.insights?.summary?.overdue || 0, color: 'text-red-600', bg: 'from-red-500/10 to-red-600/5' },
                                        { label: '완료율', value: `${briefingData?.insights?.summary?.completionRate || 0}%`, color: 'text-indigo-500', bg: 'from-indigo-500/10 to-indigo-600/5' },
                                    ].map((kpi, i) => (
                                        <div key={i} className={`rounded-xl border px-3 py-2.5 bg-gradient-to-br ${kpi.bg} ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
                                            <div className={`text-xl font-bold ${kpi.color}`}>{kpi.value}</div>
                                            <p className={`text-[10px] font-medium ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{kpi.label}</p>
                                        </div>
                                    ))}
                                </div>

                                {/* ── 프로젝트별 진행률 ── */}
                                {briefingData?.insights?.projects?.length > 0 && (
                                    <div>
                                        <h4 className={`text-xs font-bold mb-2 flex items-center gap-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                            <EmojiIcon emoji="📁" className="w-3.5 h-3.5 inline mb-0.5" /> 프로젝트 현황
                                        </h4>
                                        <div className="space-y-2">
                                            {(briefingData!.insights.projects as any[]).map((p: any, i: number) => (
                                                <div key={i} className={`rounded-xl border px-4 py-3 ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-gray-50 border-gray-200'}`}>
                                                    <div className="flex items-center justify-between mb-1.5">
                                                        <div className="flex items-center gap-2">
                                                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }}></span>
                                                            <span className={`text-xs font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{p.name}</span>
                                                        </div>
                                                        <div className="flex items-center gap-3 text-[10px]">
                                                            <span className="text-blue-500">진행 {p.inProgress}</span>
                                                            <span className="text-emerald-500">완료 {p.completed}</span>
                                                            {p.overdue > 0 && <span className="text-rose-500 font-bold">⚠ 지연 {p.overdue}</span>}
                                                            <span className={`font-bold ${p.progress >= 80 ? 'text-emerald-500' : p.progress >= 50 ? 'text-amber-500' : 'text-gray-400'}`}>{p.progress}%</span>
                                                        </div>
                                                    </div>
                                                    <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-white/5' : 'bg-gray-200'}`}>
                                                        <div className="h-full rounded-full transition-all" style={{ width: `${p.progress}%`, backgroundColor: p.color }}></div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* ── 담당자별 업무 현황 ── */}
                                {briefingData?.insights?.assignees?.length > 0 && (
                                    <div>
                                        <h4 className={`text-xs font-bold mb-2 flex items-center gap-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                            <EmojiIcon emoji="👥" className="w-3.5 h-3.5 inline mb-0.5" /> 담당자별 현황
                                        </h4>
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                                            {(briefingData!.insights.assignees as any[]).slice(0, 6).map((a: any, i: number) => (
                                                <div key={i} className={`rounded-xl border px-4 py-3 ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-gray-50 border-gray-200'}`}>
                                                    <div className="flex items-center justify-between mb-1">
                                                        <span className={`text-xs font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}><EmojiIcon emoji="🧑‍💻" className="w-3.5 h-3.5 inline mb-0.5" /> {a.name}</span>
                                                        <span className="text-[10px] text-blue-500 font-bold">{a.tasks?.length || 0}건 배정</span>
                                                    </div>
                                                    <div className="space-y-0.5">
                                                        {(a.tasks || []).slice(0, 3).map((t: any, j: number) => (
                                                            <div key={j} className="flex items-center gap-1.5 text-[10px]">
                                                                <span className={`w-1.5 h-1.5 rounded-full ${t.status === 'in_progress' || t.status === 'in-progress' ? 'bg-blue-500' : t.status === 'completed' ? 'bg-emerald-500' : 'bg-gray-400'}`}></span>
                                                                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t.title}</span>
                                                                <span className={`ml-auto ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{t.project}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {/* ── 업무 연락 로그 ── */}
                                    {briefingData?.insights?.communications?.length > 0 && (
                                        <div>
                                            <h4 className={`text-xs font-bold mb-2 flex items-center gap-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                <EmojiIcon emoji="💬" className="w-3.5 h-3.5 inline mb-0.5" /> 최근 업무 활동
                                            </h4>
                                            <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-gray-50 border-gray-200'}`}>
                                                {(briefingData!.insights.communications as any[]).slice(0, 8).map((c: any, i: number) => (
                                                    <div key={i} className={`px-3 py-2 text-[10px] flex items-center gap-2 ${i > 0 ? (isDark ? 'border-t border-white/5' : 'border-t border-gray-100') : ''}`}>
                                                        <span className={`px-1.5 py-0.5 rounded ${isDark ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-50 text-indigo-600'} font-bold`}>{c.agent}</span>
                                                        <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{c.action}</span>
                                                        {c.taskTitle && <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>"{c.taskTitle}"</span>}
                                                        <span className={`ml-auto flex-shrink-0 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{c.time}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* ── 납품/AS 팔로우업 ── */}
                                    {briefingData?.insights?.deliveryFollowUp?.length > 0 && (
                                        <div>
                                            <h4 className={`text-xs font-bold mb-2 flex items-center gap-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                <EmojiIcon emoji="📦" className="w-3.5 h-3.5 inline mb-0.5" /> 납품/AS 팔로우업
                                            </h4>
                                            <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-gray-50 border-gray-200'}`}>
                                                {(briefingData!.insights.deliveryFollowUp as any[]).map((df: any, i: number) => (
                                                    <div key={i} className={`px-3 py-2 ${i > 0 ? (isDark ? 'border-t border-white/5' : 'border-t border-gray-100') : ''}`}>
                                                        <div className={`text-[10px] font-bold mb-1 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}><EmojiIcon emoji="📁" className="w-3 h-3 inline pb-0.5" /> {df.project}</div>
                                                        {(df.deliveryTasks || []).map((dt: any, j: number) => (
                                                            <div key={j} className="flex items-center gap-1.5 text-[10px] ml-3">
                                                                <span className={`w-1.5 h-1.5 rounded-full ${dt.isOverdue ? 'bg-red-500' : dt.status === 'done' ? 'bg-emerald-500' : 'bg-amber-500'}`}></span>
                                                                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{dt.title}</span>
                                                                <span className={`ml-auto ${dt.isOverdue ? 'text-red-500 font-bold' : isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                                                                    {dt.isOverdue ? '⚠ 지연' : dt.date}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* ── 스마트 프로젝트 추천 ── */}
                                {briefingData?.insights?.smartRecommendations?.length > 0 && (
                                    <div>
                                        <h4 className={`text-xs font-bold mb-2 flex items-center gap-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                            <EmojiIcon emoji="🧠" className="w-3.5 h-3.5 inline mb-0.5" /> 과거 프로젝트 스마트 추천
                                        </h4>
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                                            {(briefingData!.insights.smartRecommendations as any[]).map((r: any, i: number) => (
                                                <div key={i} className={`rounded-xl border px-4 py-3 ${isDark ? 'bg-gradient-to-br from-purple-500/5 to-indigo-500/5 border-purple-500/20' : 'bg-purple-50 border-purple-200'}`}>
                                                    <div className="flex items-start gap-2">
                                                        <span className="text-lg"><EmojiIcon emoji="💡" className="w-4 h-4 text-inherit inline-block" /></span>
                                                        <div className="flex-1">
                                                            <div className={`text-[10px] font-bold ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>
                                                                {r.currentProject} → 참고: {r.recommendedProject}
                                                            </div>
                                                            <div className={`text-[10px] mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{r.reason}</div>
                                                            {r.nasPath && (
                                                                <div className={`text-[9px] mt-1 px-2 py-0.5 rounded inline-block ${isDark ? 'bg-white/5 text-gray-500' : 'bg-gray-100 text-gray-500'}`}>
                                                                    <EmojiIcon emoji="📂" className="w-3 h-3 inline pb-0.5" /> {r.nasPath}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* ── 마크다운 브리핑 (접기) ── */}
                                {briefingData?.markdown && (
                                    <details className={`rounded-xl border overflow-hidden ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-gray-50 border-gray-200'}`}>
                                        <summary className={`px-4 py-2 text-[10px] font-bold cursor-pointer ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}>
                                            <EmojiIcon emoji="📝" className="w-3.5 h-3.5 inline mb-0.5" /> 텍스트 브리핑 펼치기
                                        </summary>
                                        <div className={`px-4 py-3 whitespace-pre-wrap text-[10px] leading-relaxed ${isDark ? 'text-gray-400 border-t border-white/5' : 'text-gray-600 border-t border-gray-200'}`}>
                                            {briefingData.markdown}
                                        </div>
                                    </details>
                                )}
                            </div>
                        )}
                    </div>
                </motion.div>
            )}

            {/* ── 백업 관리 탭 ── */}
            {activeTab === 'backup' && (
                <motion.div key="backup" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-6 space-y-6">
                    {/* 백업 상태 카드 */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className={`rounded-xl p-5 border ${isDark ? 'bg-gray-900 border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center text-xl"><EmojiIcon emoji="🗄️" className="w-5 h-5 text-inherit inline-block" /></div>
                                <div>
                                    <h3 className={`font-bold text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>DB 프로바이더</h3>
                                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{backupStatus?.dbProvider || 'Neon PostgreSQL'}</p>
                                </div>
                            </div>
                            <div className={`text-2xl font-bold font-mono ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                                {backupStatus?.totalRecords?.toLocaleString() || '—'}
                            </div>
                            <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>총 레코드 수</p>
                        </div>
                        <div className={`rounded-xl p-5 border ${isDark ? 'bg-gray-900 border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center text-xl"><EmojiIcon emoji="🕐" className="w-5 h-5 text-inherit inline-block" /></div>
                                <div>
                                    <h3 className={`font-bold text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>마지막 백업</h3>
                                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {backupStatus?.lastBackup ? new Date(backupStatus.lastBackup).toLocaleString('ko-KR') : '없음'}
                                    </p>
                                </div>
                            </div>
                            <div className={`text-sm font-mono ${isDark ? 'text-indigo-300' : 'text-indigo-600'}`}>
                                {backupStatus?.snapshots?.length || 0}개 스냅샷
                            </div>
                        </div>
                        <div className={`rounded-xl p-5 border ${isDark ? 'bg-gray-900 border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                            <button
                                disabled={backupLoading}
                                onClick={() => {
                                    setBackupLoading(true);
                                    fetch('/api/backup', { method: 'POST' })
                                        .then(r => r.json())
                                        .then(data => {
                                            setBackupLoading(false);
                                            if (data.success) {
                                                fetch('/api/backup').then(r => r.json()).then(setBackupStatus);
                                            }
                                        })
                                        .catch(() => setBackupLoading(false));
                                }}
                                className="w-full h-full flex flex-col items-center justify-center gap-2 cursor-pointer disabled:opacity-50 group"
                            >
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-2xl group-hover:scale-110 transition-transform">
                                    {backupLoading ? '⏳' : '💾'}
                                </div>
                                <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {backupLoading ? '백업 중...' : '수동 백업 실행'}
                                </span>
                            </button>
                        </div>
                    </div>

                    {/* 테이블별 현황 */}
                    {backupStatus?.currentStats && (
                        <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-gray-900 border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                            <div className={`px-5 py-3 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                                <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>테이블별 레코드 현황</h3>
                            </div>
                            <div className="p-5 grid grid-cols-2 md:grid-cols-3 gap-3">
                                {Object.entries(backupStatus.currentStats).map(([table, count]) => (
                                    <div key={table} className={`px-4 py-3 rounded-lg ${isDark ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                                        <div className={`text-xs font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{table}</div>
                                        <div className={`text-lg font-bold font-mono mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{(count as number).toLocaleString()}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 스냅샷 목록 */}
                    {backupStatus?.snapshots?.length > 0 && (
                        <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-gray-900 border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                            <div className={`px-5 py-3 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                                <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>백업 이력</h3>
                            </div>
                            <div className="divide-y divide-white/5">
                                {backupStatus.snapshots.slice(0, 10).map((snap: any) => (
                                    <div key={snap.id} className={`px-5 py-3 flex items-center gap-4 ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50'}`}>
                                        <span className={`text-sm ${snap.status === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                                            {snap.status === 'success' ? '✅' : '❌'}
                                        </span>
                                        <div className="flex-1">
                                            <div className={`text-sm font-mono ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                {new Date(snap.timestamp).toLocaleString('ko-KR')}
                                            </div>
                                            {snap.note && <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{snap.note}</div>}
                                        </div>
                                        <div className={`text-xs font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                            {snap.totalRecords?.toLocaleString()}건
                                        </div>
                                        <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                            {snap.sizeEstimate}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </motion.div>
            )}

            {/* ── AI 리포트 모달 ── */}
            <AnimatePresence>
                {showReport && reportData && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                        onClick={() => setShowReport(false)}>
                        <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9 }}
                            className={`w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-2xl border shadow-2xl flex flex-col ${isDark ? 'bg-gray-950 border-white/10' : 'bg-white border-gray-200'}`}
                            onClick={e => e.stopPropagation()}>
                            {/* Header */}
                            <div className={`px-6 py-4 border-b flex items-center justify-between ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                                <div className="flex items-center gap-3">
                                    <span className="text-2xl"><EmojiIcon emoji="��" className="w-4 h-4 text-inherit inline-block" /></span>
                                    <div>
                                        <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{reportData?.report.title}</h3>
                                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{reportData?.report.period}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => {
                                        navigator.clipboard.writeText(reportData?.markdown);
                                        alert('클립보드에 복사되었습니다!');
                                    }} className={`text-xs px-3 py-1.5 rounded-lg border cursor-pointer ${isDark ? 'border-white/10 text-gray-400 hover:text-white' : 'border-gray-300 text-gray-600 hover:text-gray-900'}`}>
                                        <EmojiIcon emoji="��" className="w-3.5 h-3.5 inline mb-0.5" /> 복사
                                    </button>
                                    <button onClick={() => {
                                        const blob = new Blob([reportData?.markdown], { type: 'text/markdown' });
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = `report-${new Date().toISOString().slice(0, 10)}.md`;
                                        a.click();
                                    }} className={`text-xs px-3 py-1.5 rounded-lg border cursor-pointer ${isDark ? 'border-white/10 text-gray-400 hover:text-white' : 'border-gray-300 text-gray-600 hover:text-gray-900'}`}>
                                        <EmojiIcon emoji="⬇️" className="w-3.5 h-3.5 inline mb-0.5" /> 다운로드
                                    </button>
                                    <button onClick={() => setShowReport(false)} className={`text-xs px-2 py-1.5 rounded-lg cursor-pointer ${isDark ? 'text-gray-500 hover:text-white' : 'text-gray-400 hover:text-gray-900'}`}>✕</button>
                                </div>
                            </div>
                            {/* KPI Summary */}
                            <div className={`px-6 py-3 grid grid-cols-4 gap-3 border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                                {[
                                    { label: '이번 주', value: reportData?.report.summary.thisWeek, unit: '건', color: 'text-blue-500' },
                                    { label: '완료', value: `${reportData?.report.summary.done}`, unit: `(${reportData?.report.summary.completionRate}%)`, color: 'text-green-500' },
                                    { label: '다음 주', value: reportData?.report.summary.nextWeek, unit: '건', color: 'text-purple-500' },
                                    { label: '지연', value: reportData?.report.summary.overdue, unit: '건', color: reportData?.report.summary.overdue > 0 ? 'text-red-500' : 'text-gray-500' },
                                ].map(k => (
                                    <div key={k.label} className="text-center">
                                        <div className={`text-lg font-bold ${k.color}`}>{k.value}<span className={`text-xs font-normal ml-0.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{k.unit}</span></div>
                                        <div className={`text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-500'}`}>{k.label}</div>
                                    </div>
                                ))}
                            </div>
                            {/* Markdown Content */}
                            <div className="flex-1 overflow-y-auto px-6 py-4">
                                <pre className={`whitespace-pre-wrap text-sm leading-relaxed font-sans ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                    {reportData?.markdown}
                                </pre>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
            <ProjectHistoryModal 
                isOpen={!!historyModalTask} 
                onClose={() => setHistoryModalTask(null)} 
                taskTitle={historyModalTask?.title || ''}
                projectName={historyModalTask?.projectName || ''}
                projectId={historyModalTask?.projectId || ''}
                date={historyModalTask?.date || ''}
                isDark={isDark}
                onNavigateToFlow={navigateToFlowProject}
            />
            {/* ── 브리핑 히스토리 모달 ── */}
            <BriefingHistoryModal
                isOpen={isBriefingHistoryOpen}
                onClose={() => setIsBriefingHistoryOpen(false)}
                isDark={isDark}
            />

            {/* ── 작업 컨텍스트 메뉴 ── */}
            {taskContextMenu && (
                <div
                    className="fixed inset-0 z-[200]"
                    onClick={() => setTaskContextMenu(null)}
                    onContextMenu={(e) => { e.preventDefault(); setTaskContextMenu(null); }}
                >
                    <div
                        className={`absolute rounded-xl shadow-2xl border w-72 p-3 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
                        style={{
                            left: Math.min(taskContextMenu.x, window.innerWidth - 290),
                            top: Math.min(taskContextMenu.y, window.innerHeight - 300)
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h4 className={`text-sm font-bold mb-3 pb-2 border-b truncate ${isDark ? 'text-gray-200 border-gray-700' : 'text-gray-800 border-gray-100'}`}>
                            {taskContextMenu.task.title}
                        </h4>

                        {/* Quick Navigation Actions */}
                        <div className="flex gap-2 mb-3">
                            <button
                                onClick={() => {
                                    const pn = projects.find(p => p.id === taskContextMenu.task.projectId)?.name || '';
                                    setTaskContextMenu(null);
                                    navigateToFlowProject(pn);
                                }}
                                className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                                    isDark ? 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                                }`}
                            >
                                <Network className="w-3.5 h-3.5" /> 프로젝트 대시보드
                            </button>
                            <button
                                onClick={() => {
                                    const t = taskContextMenu.task;
                                    const pn = projects.find(p => p.id === t.projectId)?.name || '';
                                    setTaskContextMenu(null);
                                    setHistoryModalTask({ id: t.id, title: t.title.replace(/^[■*\s]+/, ''), projectId: t.projectId, date: t.date, projectName: pn });
                                }}
                                className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                                    isDark ? 'bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                }`}
                            >
                                <FolderOpen className="w-3.5 h-3.5" /> NAS 이력
                            </button>
                        </div>

                        <div className={`text-xs font-semibold mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>팀원 배정</div>
                        <div className="flex flex-wrap gap-1 mb-4">
                            {ASSIGNEE_OPTIONS.map((member) => {
                                const isAssigned = taskContextMenu.task.assignees?.includes(member);
                                return (
                                    <button
                                        key={member}
                                        onClick={() => toggleAssignee(member)}
                                        className={`px-2 py-1 rounded text-xs transition-colors ${isAssigned
                                            ? (isDark ? 'bg-indigo-500 text-white font-bold' : 'bg-indigo-500 text-white font-bold')
                                            : (isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')
                                        }`}
                                    >
                                        {member}
                                    </button>
                                );
                            })}
                        </div>

                        <div className={`text-xs font-semibold mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>하위 작업 메모</div>
                        <textarea
                            id="task-memo-input"
                            className={`w-full rounded p-2 text-xs outline-none focus:ring-1 border transition-colors ${
                                isDark 
                                ? 'bg-gray-900 border-gray-700 text-gray-200 focus:ring-indigo-500 focus:border-indigo-500' 
                                : 'bg-gray-50 border-gray-200 text-gray-800 focus:ring-indigo-500 focus:border-indigo-500'
                            }`}
                            rows={3}
                            placeholder="작업 분담이나 메모를 남겨주세요..."
                            defaultValue={taskContextMenu.task?.memo || ''}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                    updateTaskMemo(e.currentTarget.value);
                                    setTaskContextMenu(null);
                                }
                            }}
                        />
                        <div className="flex justify-between items-center mt-3">
                            <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                Cmd+Enter로 저장
                            </div>
                            <button
                                onClick={() => {
                                    const val = (document.getElementById('task-memo-input') as HTMLTextAreaElement)?.value || '';
                                    updateTaskMemo(val);
                                    setTaskContextMenu(null);
                                }}
                                className="px-4 py-1.5 rounded-lg text-xs font-bold bg-indigo-500 hover:bg-indigo-600 text-white shadow shadow-indigo-500/30 transition-all cursor-pointer"
                            >
                                저장
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── 하위 작업 (요청) 추가 모달 ── */}
            {subtaskAddTask && (
                <div
                    className="fixed inset-0 z-[200]"
                    onClick={() => setSubtaskAddTask(null)}
                >
                    <div
                        className={`absolute rounded-xl shadow-2xl border w-72 p-4 flex flex-col gap-3 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
                        style={{
                            left: Math.min(subtaskAddTask.x, window.innerWidth - 290),
                            top: Math.min(subtaskAddTask.y, window.innerHeight - 300)
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h4 className={`text-sm font-bold border-b pb-2 ${isDark ? 'text-gray-200 border-gray-700' : 'text-gray-800 border-gray-100'}`}>
                            하위 업무 추가 (플로우 요청)
                        </h4>
                        <div>
                            <label className={`text-[10px] font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>업무 내용</label>
                            <input
                                autoFocus
                                value={subtaskTitle}
                                onChange={e => setSubtaskTitle(e.target.value)}
                                className={`w-full text-xs rounded p-2 mt-1 outline-none border transition-colors ${
                                    isDark ? 'bg-gray-900 border-gray-700 text-gray-200 focus:border-emerald-500' : 'bg-gray-50 border-gray-200 focus:border-emerald-500'
                                }`}
                                placeholder="요청할 하위 업무명"
                            />
                        </div>
                        <div>
                            <label className={`text-[10px] font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>담당자 (팀원 지정 시 알림 발송)</label>
                            <select
                                value={subtaskAssignee}
                                onChange={e => setSubtaskAssignee(e.target.value)}
                                className={`w-full text-xs rounded p-2 mt-1 outline-none border transition-colors ${
                                    isDark ? 'bg-gray-900 border-gray-700 text-gray-200 focus:border-emerald-500' : 'bg-gray-50 border-gray-200 focus:border-emerald-500'
                                }`}
                            >
                                <option value="">지정 안 함</option>
                                {ASSIGNEE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                        <div className="flex gap-2 justify-end mt-2">
                            <button
                                onClick={() => setSubtaskAddTask(null)}
                                className={`px-3 py-1.5 rounded text-xs transition-colors ${isDark ? 'text-gray-300 hover:bg-white/10' : 'text-gray-500 hover:bg-gray-100'}`}
                            >
                                취소
                            </button>
                            <button
                                onClick={async () => {
                                    if (!subtaskTitle.trim()) return;
                                    const newTaskObj = {
                                        title: subtaskTitle.trim(),
                                        projectId: subtaskAddTask.task.projectId,
                                        date: subtaskAddTask.task.date,
                                        status: 'todo',
                                        assignees: subtaskAssignee || undefined
                                    };
                                    try {
                                        const res = await fetch('/api/schedule/tasks', {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify(newTaskObj)
                                        });
                                        if (res.ok) {
                                            const data = await res.json();
                                            if (data.task) setTasks(prev => [...prev, data.task]);
                                            
                                            addHistoryLog({
                                                time: '방금 전', agent: 'Shon',
                                                msg: `업무 분장/요청(${newTaskObj.title}) 추가됨`,
                                                channels: ['콘솔'], type: 'add'
                                            });
                                            if (subtaskAssignee) {
                                                sendNotification({ add: [{ title: newTaskObj.title, date: newTaskObj.date, assignees: newTaskObj.assignees, projectId: newTaskObj.projectId, projectName: projects.find(p=>p.id===newTaskObj.projectId)?.name }] });
                                            }
                                        }
                                    } catch (err) {
                                        console.error(err);
                                    }
                                    setSubtaskAddTask(null);
                                }}
                                className="px-3 py-1.5 rounded text-xs font-bold bg-emerald-500 hover:bg-emerald-600 text-white shadow shadow-emerald-500/30 transition-all"
                            >
                                업무 추가
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── 에이전트 챗봇 팝업 (프리미엄) ── */}
            {showAgentChat && (
                <AgentChatPanel
                    isDark={isDark}
                    projectId={defaultProjectId || undefined}
                    projectName={defaultProjectId ? projects.find(p => p.id === defaultProjectId)?.name : undefined}
                    onClose={() => setShowAgentChat(false)}
                />
            )}

            {/* ── 코딩 (DevOps Board) ── */}
            {activeTab === 'coding' && (
                <CodingBoard isDark={isDark} />
            )}
            {activeTab === 'training' && (
                <motion.div key="training" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-6">
                    <AgentTrainingBoard isDark={isDark} />
                </motion.div>
            )}
            <JumpToToday />
        </div>
    );
}
